import { SelectedStyle } from '@blocksuite/affine-components/caption';
import {
  menu,
  popFilterableSimpleMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { toast } from '@blocksuite/affine-components/toast';
import {
  type CanvasDatabasePresentationState,
  type CanvasDatabasePresentationViewportTarget,
  type DatabaseBlockModel,
  DatabaseBlockSchema,
  type ViewBasicDataType,
} from '@blocksuite/affine-model';
import { EDGELESS_CANVAS_CARD_SHADOW } from '@blocksuite/affine-shared/consts';
import {
  NotificationProvider,
  type TelemetryEventMap,
  TelemetryProvider,
} from '@blocksuite/affine-shared/services';
import { STREAMLAIN_COMPACT_HEADER_HEIGHT } from '@blocksuite/affine-shared/styles';
import {
  getTypeColor,
  inferMimeTypeFromName,
  normalizeTypeLabel,
  resolveBlobDisplayName,
} from '@blocksuite/affine-shared/utils';
import {
  checkDataViewBodyContract,
  DATA_VIEW_BODY_INTRINSIC_ATTRIBUTE,
  DATA_VIEW_BODY_RENDER_CONTENT_ATTRIBUTE,
  DATA_VIEW_BODY_RENDER_ROOT_ATTRIBUTE,
  DATA_VIEW_BODY_STATE_ATTRIBUTE,
  DATA_VIEW_FRAME_ATTRIBUTE,
  DATA_VIEW_FRAME_BODY_ATTRIBUTE,
  DATA_VIEW_FRAME_FOOTER_ATTRIBUTE,
  DATA_VIEW_FRAME_TOP_ATTRIBUTE,
  DATABASE_ATTACHMENT_CELL_FILES_EVENT,
  DATABASE_LIVE_VIEW_INTRINSIC_HEIGHT_CHANGE_EVENT,
  DATABASE_LIVE_VIEW_SELECTOR,
  DATABASE_LIVE_VIEW_SETUP_STATE_CHANGE_EVENT,
  type DatabaseAttachmentCellFilesEvent,
  dataViewI18nT,
  DataViewRootUILogic,
  DataViewSearchCollaboration,
  type DataViewSearchUiState,
  type DataViewSelection,
  type DataViewWidget,
  type DataViewWidgetProps,
  defineUniComponent,
  ExternalGroupByConfigProvider,
  getClipboardFiles,
  getDataViewFrameElement,
  lazy,
  measureDataViewFrameMetrics,
  offerBlobReferencesToSelectedAttachmentCell,
  offerFilesToSelectedAttachmentCell,
  renderUniLit,
  type SingleView,
} from '@blocksuite/data-view';
import { TableSingleView } from '@blocksuite/data-view/view-presets';
import { Bound } from '@blocksuite/global/gfx';
import { ArrowDownSmallIcon } from '@blocksuite/icons/lit';
import {
  getGfxBlockElementModelSize,
  toGfxBlockComponent,
} from '@blocksuite/std';
import {
  GfxControllerIdentifier,
  GfxViewInteractionExtension,
  measureGfxPerformance,
} from '@blocksuite/std/gfx';
import type { BlockSnapshot, SliceSnapshot } from '@blocksuite/store';
import { computed, effect, signal } from '@preact/signals-core';
import { isFileIndexDatabaseId } from '@streamlain/database-identity';
import { html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  CANVAS_DATABASE_VERTICAL_PADDING,
  DEFAULT_CANVAS_DATABASE_HEIGHT,
  DEFAULT_CANVAS_DATABASE_WIDTH,
  MAX_CANVAS_DATABASE_HEIGHT,
  MIN_CANVAS_DATABASE_HEIGHT,
} from './canvas-defaults';
import {
  resolveCanvasDatabasePresentationInteractivity,
  shouldActivateCanvasDatabaseSelectionFromPointer,
  shouldFitCanvasDatabaseFromHeaderClick,
  shouldKeepCanvasDatabaseActionSelectionNonEditing,
  shouldRouteCanvasDatabasePointerToCanvasTool,
} from './canvas-interaction-contract';
import {
  type CanvasDatabasePresentationKind,
  resolveCanvasDatabasePresentationReadiness,
  shouldRunCanvasDatabasePresentationWork,
  shouldScheduleCanvasDatabasePresentationSync,
} from './canvas-presentation-readiness';
import {
  CanvasDatabasePresentationScheduler,
  type CanvasDatabaseSyncReason,
} from './canvas-presentation-scheduler';
import {
  createCanvasLivePresentationState,
  createCanvasPresentationFeatureFlags,
  createCanvasSummaryPresentationState,
  getCanvasPresentationState,
  getRepairedCanvasPresentationState,
  quantizeCanvasViewport,
  resolveCanvasPresentation,
  shouldApplyObservedCanvasViewport,
  updateCanvasPresentationState,
} from './canvas-presentation-state';
import { syncAutoMeasuredTransientXYWH } from './canvas-transient-geometry';
import { popSideDetail } from './components/layout';
import { EditorHostKey } from './context/host-context';
import {
  DatabaseBlockDataSource,
  type DatabaseRowShardHydrationProgress,
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
} from './data-source';
import { DatabaseBlockComponent } from './database-block';
import { databaseBlockStyles } from './database-block-styles';
import * as styles from './database-canvas-block.css';
import {
  addRowWithAttachment,
  assertCanAppendAttachmentRows,
  type AttachmentBlobInfo,
  uploadFileAsBlob,
} from './database-canvas-drop-handler';
import { renderDatabaseDataViewChrome } from './database-data-view-chrome';
import {
  isFileIndexDatabase,
  isHiddenSystemManagedDatabaseBlock,
} from './file-index-database';
import { DatabaseSelection } from './selection';

/**
 * Shape of the drag payload produced by the drag handle widget.
 * Defined locally to avoid coupling to the widget's internal types.
 */
interface DragBlockPayload {
  bsEntity?: {
    type: string;
    fromMode?: string;
    snapshot?: SliceSnapshot;
    modelIds?: string[];
  };
  from?: {
    docId?: string;
  };
}

const IMPORTABLE_FLAVOURS = new Set(['affine:image', 'affine:attachment']);

function isImportableBlock(block: BlockSnapshot): boolean {
  return IMPORTABLE_FLAVOURS.has(block.flavour);
}

function extractBlobFromSnapshot(
  block: BlockSnapshot
): AttachmentBlobInfo | null {
  const props = block.props as Record<string, unknown>;
  const sourceId = props.sourceId as string | undefined;
  if (!sourceId) return null;

  if (block.flavour === 'affine:image') {
    const occurrenceName =
      typeof props.name === 'string' && props.name.trim()
        ? props.name.trim()
        : '';
    const rawMime =
      typeof props.type === 'string' && props.type.trim()
        ? props.type.trim()
        : '';
    const mime =
      rawMime && rawMime !== 'application/octet-stream'
        ? rawMime
        : (inferMimeTypeFromName(occurrenceName) ?? 'application/octet-stream');
    return {
      blobId: sourceId,
      name: resolveBlobDisplayName({
        occurrenceName,
        fallbackName: 'image',
        mimeType: mime,
      }),
      mime,
      size: (props.size as number) || 0,
    };
  }
  if (block.flavour === 'affine:attachment') {
    return {
      blobId: sourceId,
      name: (props.name as string) || 'attachment',
      mime: (props.type as string) || 'application/octet-stream',
      size: (props.size as number) || 0,
    };
  }
  return null;
}

export const AFFINE_EDGELESS_DATABASE = 'affine-edgeless-database';

const SUMMARY_EXCLUDED_PROPERTY_TYPES = new Set([
  'type',
  'created-time',
  'created-by',
  'updated-time',
  'updated-by',
]);

const LIVE_CANVAS_VIEW_MODES = new Set([
  'table',
  'kanban',
  'gantt',
  'calendar',
]);
const CANVAS_VIEWPORT_SYNC_DELAY = 120;
const CANVAS_HEADER_CLICK_MOVE_TOLERANCE = 4;
const CANVAS_DATABASE_SETUP_VIEWPORT_PADDING: [number, number, number, number] =
  [80, 80, 80, 80];
const PRESENTATION_ROOT_SCROLL_TARGET = 'body';
const CANVAS_VIEWPORT_PARTICIPANT_SELECTOR =
  '[data-canvas-viewport-participant]';
const CANVAS_VIEWPORT_PARTICIPANT_READY_EVENT =
  'canvas-viewport-participant-ready';
const CANVAS_DATABASE_LIVE_ROW_HEIGHT = 36;
const CANVAS_DATABASE_PRESENTATION_HEADER_HEIGHT =
  STREAMLAIN_COMPACT_HEADER_HEIGHT;
const CANVAS_DATABASE_TABLE_HEADER_HEIGHT = 34;
const CANVAS_DATABASE_TABLE_GROUP_FOOTER_HEIGHT = 71;
const CANVAS_DATABASE_ROW_WINDOW_FOOTER_HEIGHT = 52;
const CANVAS_HEIGHT_CHANGE_THRESHOLD = 1;
const CANVAS_DATABASE_LIVE_CHROME_HEIGHT =
  CANVAS_DATABASE_PRESENTATION_HEADER_HEIGHT +
  CANVAS_DATABASE_TABLE_HEADER_HEIGHT +
  CANVAS_DATABASE_TABLE_GROUP_FOOTER_HEIGHT +
  CANVAS_DATABASE_ROW_WINDOW_FOOTER_HEIGHT;
const LIVE_PRESENTATION_BODY_CONTRACT_STABLE_FRAME_THRESHOLD = 2;
const FILE_INDEX_CANVAS_DEFAULT_HEIGHT = 420;
const DATABASE_CANVAS_LAYOUT_STATE_STORAGE_KEY =
  'sl:database-canvas-layout-state:v1';
const DATABASE_CANVAS_LAYOUT_STATE_VERSION = 3 as const;
const DATABASE_LOAD_MARK_PREFIX = 'sl-database-load';

type DatabaseCanvasLayoutState = {
  height: number;
  measuredAt: number;
  version: typeof DATABASE_CANVAS_LAYOUT_STATE_VERSION;
};

function getDatabaseCanvasLayoutStateKey(docId: string, blockId: string) {
  return `${DATABASE_CANVAS_LAYOUT_STATE_STORAGE_KEY}:${docId}:${blockId}`;
}

function getDatabaseCanvasPresentationLayoutStateKey(
  docId: string,
  blockId: string,
  presentationKey: string
) {
  return `${getDatabaseCanvasLayoutStateKey(docId, blockId)}:${presentationKey}`;
}

function readDatabaseCanvasLayoutState(
  docId: string,
  blockId: string,
  presentationKey: string
): DatabaseCanvasLayoutState | undefined {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    const raw = localStorage.getItem(
      getDatabaseCanvasPresentationLayoutStateKey(
        docId,
        blockId,
        presentationKey
      )
    );
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw) as Partial<DatabaseCanvasLayoutState>;
    if (
      parsed.version !== DATABASE_CANVAS_LAYOUT_STATE_VERSION ||
      typeof parsed.height !== 'number' ||
      !Number.isFinite(parsed.height) ||
      parsed.height <= 0
    ) {
      return;
    }
    return {
      height: parsed.height,
      measuredAt:
        typeof parsed.measuredAt === 'number' &&
        Number.isFinite(parsed.measuredAt)
          ? parsed.measuredAt
          : 0,
      version: DATABASE_CANVAS_LAYOUT_STATE_VERSION,
    };
  } catch {
    return;
  }
}

function writeDatabaseCanvasLayoutState(
  docId: string,
  blockId: string,
  presentationKey: string,
  height: number
): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (!Number.isFinite(height) || height <= 0) {
    return;
  }
  try {
    localStorage.setItem(
      getDatabaseCanvasPresentationLayoutStateKey(
        docId,
        blockId,
        presentationKey
      ),
      JSON.stringify({
        height,
        measuredAt: Date.now(),
        version: DATABASE_CANVAS_LAYOUT_STATE_VERSION,
      } satisfies DatabaseCanvasLayoutState)
    );
  } catch {
    // Browser storage is best-effort local UI state.
  }
}

function removeDatabaseCanvasLayoutState(
  docId: string,
  blockId: string,
  presentationKey: string
): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(
      getDatabaseCanvasPresentationLayoutStateKey(
        docId,
        blockId,
        presentationKey
      )
    );
  } catch {
    // Browser storage is best-effort local UI state.
  }
}

function markCanvasDatabaseLoad(
  event: string,
  data: Record<string, unknown>
): void {
  if (
    typeof performance === 'undefined' ||
    typeof performance.mark !== 'function'
  ) {
    return;
  }

  try {
    performance.mark(
      `${DATABASE_LOAD_MARK_PREFIX}:${event}:${JSON.stringify(data)}`
    );
  } catch {
    // Performance marks are diagnostics only.
  }
}

function createFallbackCanvasDatabaseBound() {
  return new Bound(
    0,
    0,
    DEFAULT_CANVAS_DATABASE_WIDTH,
    MIN_CANVAS_DATABASE_HEIGHT
  );
}

function isValidCanvasDatabaseBound(bound: Bound) {
  return (
    Number.isFinite(bound.x) &&
    Number.isFinite(bound.y) &&
    Number.isFinite(bound.w) &&
    Number.isFinite(bound.h) &&
    bound.w > 0 &&
    bound.h > 0
  );
}

const DATABASE_AUTO_PERSIST_DEFAULT_BOUNDS = [
  // DatabaseBlockSchema's legacy default.
  new Bound(0, 0, 420, 300),
  new Bound(
    0,
    0,
    DEFAULT_CANVAS_DATABASE_WIDTH,
    DEFAULT_CANVAS_DATABASE_HEIGHT
  ),
  new Bound(0, 0, DEFAULT_CANVAS_DATABASE_WIDTH, MIN_CANVAS_DATABASE_HEIGHT),
];

function isDefaultCanvasDatabaseBound(bound: Bound) {
  return DATABASE_AUTO_PERSIST_DEFAULT_BOUNDS.some(
    defaultBound =>
      bound.x === defaultBound.x &&
      bound.y === defaultBound.y &&
      bound.w === defaultBound.w &&
      bound.h === defaultBound.h
  );
}

function hasAutoSizedInitialCanvasDatabaseHeight(bound: Bound) {
  return (
    isDefaultCanvasDatabaseBound(bound) ||
    bound.h === FILE_INDEX_CANVAS_DEFAULT_HEIGHT
  );
}

function deserializeCanvasDatabaseBound(xywh: unknown) {
  if (typeof xywh !== 'string') {
    return createFallbackCanvasDatabaseBound();
  }

  try {
    const bound = Bound.deserialize(xywh);
    return isValidCanvasDatabaseBound(bound)
      ? bound
      : createFallbackCanvasDatabaseBound();
  } catch {
    return createFallbackCanvasDatabaseBound();
  }
}

type CanvasViewportParticipant = HTMLElement & {
  applyCanvasViewportTarget?: (
    targetId: string,
    position: CanvasDatabasePresentationViewportTarget
  ) => boolean | void;
  getCanvasViewportTargets?: () => Record<
    string,
    CanvasDatabasePresentationViewportTarget
  >;
};

type CanvasViewportTargetEvent = CustomEvent<{
  position: CanvasDatabasePresentationViewportTarget;
  targetId: string;
}>;

type CanvasViewWindowState = {
  propertyLimit?: number;
  propertyOffset?: number;
  rowLimit?: number;
  rowOffset?: number;
};

type CanvasViewSearchState = DataViewSearchUiState;

type CanvasUiAwarenessState = {
  databaseId: string;
  presentation: CanvasDatabasePresentationState;
  searches?: Record<string, CanvasViewSearchState>;
  scope: string;
  updatedAt: number;
  windows?: Record<string, CanvasViewWindowState>;
};

class CanvasPresentationDatabaseBlockDataSource extends DatabaseBlockDataSource {
  private readonly canvasViewSearchStates$ = signal<
    Record<string, CanvasViewSearchState>
  >({});

  private readonly canvasViewWindowStates$ = signal<
    Record<string, CanvasViewWindowState>
  >({});

  constructor(
    model: DatabaseBlockModel,
    init?: (dataSource: DatabaseBlockDataSource) => void,
    private readonly onCanvasWindowStateChange?: () => void
  ) {
    super(model, init);
    this.setUiStatePersistence('transient');
    const baseFeatureFlags$ = this.featureFlags$;
    this.featureFlags$ = computed(() =>
      createCanvasPresentationFeatureFlags(baseFeatureFlags$.value)
    );
    this.serviceSet(DataViewSearchCollaboration, {
      states$: this.canvasViewSearchStates$,
      update: (viewId, state) => {
        this.canvasViewSearchStates$.value = {
          ...this.canvasViewSearchStates$.value,
          [viewId]: state,
        };
        this.onCanvasWindowStateChange?.();
      },
    });
  }

  getCanvasViewSearchStates(): Record<string, CanvasViewSearchState> {
    return this.canvasViewSearchStates$.value;
  }

  setCanvasViewSearchStates(
    states: Record<string, CanvasViewSearchState> = {}
  ): void {
    this.canvasViewSearchStates$.value = states;
  }

  getCanvasViewWindowStates(): Record<string, CanvasViewWindowState> {
    return this.canvasViewWindowStates$.value;
  }

  setCanvasViewWindowStates(
    states: Record<string, CanvasViewWindowState> = {}
  ): void {
    this.canvasViewWindowStates$.value = states;
  }

  override getViewWindowState(
    viewId: string
  ): CanvasViewWindowState | undefined {
    return this.canvasViewWindowStates$.value[viewId];
  }

  override setViewWindowState(
    viewId: string,
    patch: CanvasViewWindowState
  ): boolean {
    this.canvasViewWindowStates$.value = {
      ...this.canvasViewWindowStates$.value,
      [viewId]: {
        ...(this.canvasViewWindowStates$.value[viewId] ?? {}),
        ...patch,
      },
    };
    this.onCanvasWindowStateChange?.();
    return true;
  }
}

export class EdgelessDatabaseBlockComponent extends toGfxBlockComponent(
  DatabaseBlockComponent
) {
  protected override accessor selectedStyle = SelectedStyle.Border;
  protected override accessor blockContainerStyles = {
    height: '100%',
    overflow: 'hidden',
    width: '100%',
  };

  private _canvasRuntimeInitialized = false;

  private _canvasHeightAutoSizingSetup = false;

  private _queueCanvasHeightUpdate: (() => void) | null = null;

  private _syncCanvasHeightObserverTargets: (() => void) | null = null;

  private readonly _postUpdateCanvasSyncScheduler =
    new CanvasDatabasePresentationScheduler(reasons => {
      if (!this.isConnected) {
        return;
      }
      measureGfxPerformance(
        this.std,
        'database-presentation-sync',
        () => this.runPostUpdateCanvasSync(reasons),
        () => this._rowCount
      );
    });

  private _autoMeasuredTransientXYWH: string | null = null;

  private getCanvasDatabaseRuntimeState() {
    const blockId = this.blockId;
    const store = this.store;
    const std = this.std;
    if (!blockId || !store || !std) return null;

    const model = store.getModelById<DatabaseBlockModel>(blockId);
    const gfx = std.getOptional(GfxControllerIdentifier);
    if (!model || !gfx) return null;

    return { gfx, model };
  }

  private getCanvasDatabaseBound(model?: DatabaseBlockModel | null) {
    const targetModel = model ?? this.getCanvasDatabaseRuntimeState()?.model;
    if (!targetModel) return createFallbackCanvasDatabaseBound();

    const xywh =
      targetModel.transientXYWH ??
      (typeof targetModel.xywh$?.value === 'string'
        ? targetModel.xywh$.value
        : targetModel.props.xywh);
    const bound = deserializeCanvasDatabaseBound(xywh);
    return bound;
  }

  private emitAutoMeasuredTransientGeometryUpdated() {
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state) return;
    state.gfx.grid.update(state.model);
    state.gfx.selection.slots.transientGeometryUpdated.next(state.model.id);
  }

  private setAutoMeasuredTransientXYWH(nextXYWH: string | null) {
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state) return;

    this._autoMeasuredTransientXYWH = syncAutoMeasuredTransientXYWH({
      model: state.model,
      nextXYWH,
      ownedXYWH: this._autoMeasuredTransientXYWH,
      effects: {
        refreshBlockHost: () => this.refreshGfxTransform(),
        emitUpdated: () => this.emitAutoMeasuredTransientGeometryUpdated(),
        requestRender: () => this.requestUpdate(),
      },
    });
  }

  private rebaseAutoMeasuredTransientXYWH() {
    const state = this.getCanvasDatabaseRuntimeState();
    if (
      !state ||
      !this._autoMeasuredTransientXYWH ||
      state.model.transientXYWH !== this._autoMeasuredTransientXYWH
    ) {
      return;
    }

    const baseBound = deserializeCanvasDatabaseBound(state.model.props.xywh);
    const transientBound = deserializeCanvasDatabaseBound(
      this._autoMeasuredTransientXYWH
    );
    transientBound.x = baseBound.x;
    transientBound.y = baseBound.y;
    transientBound.w = baseBound.w;
    this.setAutoMeasuredTransientXYWH(transientBound.serialize());
  }

  private focusCanvasDatabaseForSetupTransition() {
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state) return;

    state.gfx.selection.set({
      elements: [state.model.id],
      editing: true,
    });
  }

  private alignCanvasDatabaseAfterSetupTransition() {
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state) return;

    state.gfx.viewport.setViewportByBound(
      this.getCanvasDatabaseBound(state.model),
      CANVAS_DATABASE_SETUP_VIEWPORT_PADDING,
      true
    );
  }

  private readonly fitCanvasDatabaseFromHeaderClick = (event: MouseEvent) => {
    const pointerDown = this._canvasHeaderPointerDown;
    this._canvasHeaderPointerDown = null;
    const hasDragged =
      pointerDown !== null &&
      Math.hypot(
        event.clientX - pointerDown.clientX,
        event.clientY - pointerDown.clientY
      ) > CANVAS_HEADER_CLICK_MOVE_TOLERANCE;
    const isInteractiveTarget = event.composedPath().some(target => {
      if (!(target instanceof Element)) return false;
      const zone = target.getAttribute('data-canvas-db-zone');
      if (zone && zone !== 'select-move') return true;
      return target.matches(
        'button, input, textarea, select, a[href], [role="button"], [contenteditable="true"]'
      );
    });
    if (
      !shouldFitCanvasDatabaseFromHeaderClick({
        button: event.button,
        hasDragged,
        hasModifier:
          event.shiftKey || event.ctrlKey || event.metaKey || event.altKey,
        isInteractiveTarget,
      })
    ) {
      return;
    }

    const state = this.getCanvasDatabaseRuntimeState();
    if (!state) return;
    event.stopPropagation();
    state.gfx.viewport.setViewportByBound(
      this.getCanvasDatabaseBound(state.model),
      CANVAS_DATABASE_SETUP_VIEWPORT_PADDING,
      true
    );
  };

  private readonly rememberCanvasHeaderPointerDown = (event: PointerEvent) => {
    this._canvasHeaderPointerDown =
      event.button === 0 && event.isPrimary
        ? { clientX: event.clientX, clientY: event.clientY }
        : null;
  };

  private initializeCanvasDatabaseRuntime() {
    if (this._canvasRuntimeInitialized) return;
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state) return;
    const { gfx, model } = state;

    let previousTransformState = this.transformState$.value;
    this._disposables.add(
      effect(() => {
        const transformState = this.transformState$.value;
        if (transformState === previousTransformState) {
          return;
        }
        previousTransformState = transformState;
        if (transformState === 'active' && this.isConnected) {
          this.requestUpdate();
          this.queuePostUpdateCanvasSync('transform-reentry');
        }
      })
    );

    const selection = gfx.selection;

    this._selected = selection.has(model.id);
    this._editing = selection.has(model.id) && selection.editing;
    this._disposables.add(
      selection.slots.updated.subscribe(() => {
        this._selected = selection.has(model.id);
        this._editing = this._selected && selection.editing;
      })
    );
    this._disposables.add(
      model.propsUpdated.subscribe(({ key }) => {
        if (key === 'xywh') {
          this.rebaseAutoMeasuredTransientXYWH();
        }
      })
    );
    this.subscribeCanvasToolState();
    this._disposables.add(() => {
      this._canvasRuntimeInitialized = false;
      this._canvasToolSubscriptionSetup = false;
      this._canvasToolSubscriptionAttempts = 0;
      this._canvasUiAwarenessSetup = false;
      this._postUpdateCanvasSyncScheduler.dispose();
      this.clearPendingPresentationViewport();
      this.clearLocalCanvasUiAwareness();
      this.teardownCanvasDatabaseStorageReadyObserver();
      this.cleanupPresentationScrollHostObservers();
      this.cleanupPresentationScrollTargets();
      this.clearPresentationViewportRestoreTimers();
      this._presentationScrollHost = null;
    });

    this.ensureCanvasDataSourceSubscriptions();
    this._registerAsDropTarget();

    const host = this.std.host;
    const onAttachmentCellFiles = (event: Event) => {
      if (event.defaultPrevented) return;
      if (!this._isDatabaseSelected()) return;
      const files = (event as DatabaseAttachmentCellFilesEvent).detail?.files;
      if (!files?.length) return;

      if (this.pasteFilesIntoSelectedAttachmentCell(files)) {
        event.preventDefault();
      }
    };

    const onHostPaste = (event: Event) => {
      const clipboardEvent = event as ClipboardEvent;
      if (!this._isDatabaseSelected()) return;
      const data = clipboardEvent.clipboardData;
      if (!data) return;

      const files = getClipboardFiles(data);
      if (files.length > 0) {
        if (offerFilesToSelectedAttachmentCell(files)) {
          event.stopImmediatePropagation();
          event.stopPropagation();
          event.preventDefault();
          return;
        }
        if (this.pasteFilesIntoSelectedAttachmentCell(files)) {
          event.stopImmediatePropagation();
          event.stopPropagation();
          event.preventDefault();
          return;
        }
        event.stopPropagation();
        event.preventDefault();
        this._importFiles(files).catch(error =>
          this._handleImportFilesError(error)
        );
        return;
      }

      try {
        if (!this._importFromBlockSuiteClipboard(data)) return;
        event.stopPropagation();
        event.preventDefault();
      } catch (error) {
        event.stopPropagation();
        event.preventDefault();
        this._notifyImportError(error);
      }
    };
    host.addEventListener('paste', onHostPaste, true);
    window.addEventListener(
      DATABASE_ATTACHMENT_CELL_FILES_EVENT,
      onAttachmentCellFiles
    );
    this._disposables.add(() => {
      host.removeEventListener('paste', onHostPaste, true);
      window.removeEventListener(
        DATABASE_ATTACHMENT_CELL_FILES_EVENT,
        onAttachmentCellFiles
      );
    });
    this._canvasRuntimeInitialized = true;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // The custom-element host is the outer card shell. Its own overflow clips
    // the database viewport, while its shadow still paints outside that box.
    // Putting the shadow on a rendered child clips it at the host boundary.
    this.style.borderRadius = '12px';
    this.style.boxShadow = EDGELESS_CANVAS_CARD_SHADOW;
    this.style.overflow = 'hidden';
    if (isFileIndexDatabaseId(this.blockId)) {
      this.style.display = 'none';
    }
    this.syncSystemManagedCanvasVisibility();

    // Remove doc-mode styles that break canvas positioning (negative margins)
    this.classList.remove(databaseBlockStyles);

    this.initializeCanvasDatabaseRuntime();
    if (this.isCanvasDatabasePresentationWorkActive()) {
      this.syncCanvasDatabaseBoundWithActivePresentationHeight();
    }
  }

  override firstUpdated() {
    this.setupCanvasHeightAutoSizing();
  }

  private setupCanvasHeightAutoSizing() {
    if (this._canvasHeightAutoSizingSetup) {
      return;
    }
    if (this.transformState$.value !== 'active') return;
    if (!this.getCanvasDatabaseRuntimeState()) return;
    // Model height normalization writes xywh and must run outside Lit's
    // firstUpdated cycle; updated() queues the post-update sync for that.

    // Update model height when content size changes
    const container = this.renderRoot.querySelector(
      '.edgeless-database-content'
    ) as HTMLElement | null;
    if (!container) return;
    this._canvasHeightAutoSizingSetup = true;

    let heightUpdateFrame: ReturnType<typeof requestAnimationFrame> | null =
      null;
    let setupViewportAlignmentPending = false;
    const updateHeight = () => {
      heightUpdateFrame = null;
      if (this.transformState$.value !== 'active') {
        return;
      }
      if (!this.isCanvasDatabaseStorageReady()) {
        return;
      }
      const { height: renderedContentHeight } =
        getGfxBlockElementModelSize(container);
      if (!renderedContentHeight && !container.scrollHeight) return;

      const state = this.getCanvasDatabaseRuntimeState();
      if (!state) return;

      const bound = this.getCanvasDatabaseBound(state.model);
      const zoom = state.gfx.viewport.zoom || 1;
      const measuredContentHeight = Math.max(
        renderedContentHeight,
        container.scrollHeight
      );
      const fallbackContentHeight =
        measuredContentHeight + CANVAS_DATABASE_VERTICAL_PADDING;
      const presentation = this.resolveCanvasPresentation();
      const isInitialCanvasPresentation =
        this._lastCanvasPresentationKey === null;
      this.markCanvasPresentationKey(presentation.state);
      const presentationKey = this.getCanvasPresentationStateKey(
        presentation.state
      );
      const isLivePresentation = presentation.kind === 'live';
      const contentReadiness = isLivePresentation
        ? this.canvasDataSource.value.getContentReadiness()
        : { status: 'ready' as const };
      if (contentReadiness.status !== 'ready') {
        return;
      }
      const liveView = isLivePresentation ? presentation.view : undefined;
      const liveIntrinsicHeight = this.measureLivePresentationIntrinsicHeight(
        container,
        zoom,
        liveView?.mode
      );
      const summaryIntrinsicHeight =
        presentation.kind === 'summary'
          ? this.measureSummaryPresentationIntrinsicHeight(container)
          : 0;
      const allowLivePresentationShrink =
        isLivePresentation &&
        liveIntrinsicHeight > 0 &&
        (this.consumeLivePresentationShrinkAllowance(presentationKey) ||
          (isInitialCanvasPresentation &&
            hasAutoSizedInitialCanvasDatabaseHeight(bound)));
      const cachedContentHeight = liveView
        ? this.getCachedLivePresentationContentHeight(liveView)
        : 0;
      const estimatedContentHeight =
        this.estimateLivePresentationContentHeight();
      const contentHeight = isLivePresentation
        ? this.stabilizeLivePresentationContentHeight(
            liveIntrinsicHeight,
            cachedContentHeight,
            estimatedContentHeight,
            fallbackContentHeight,
            bound.h,
            allowLivePresentationShrink
          )
        : summaryIntrinsicHeight || fallbackContentHeight;
      const boundedHeight = this.toBoundedCanvasHeight(
        Math.round(contentHeight)
      );
      const isHeightCapped = boundedHeight >= MAX_CANVAS_DATABASE_HEIGHT - 1;
      this.setCanvasHeightCapped(isHeightCapped);

      // Only update if height changed significantly (avoid loops)
      if (Math.abs(bound.h - boundedHeight) > CANVAS_HEIGHT_CHANGE_THRESHOLD) {
        this.applyCanvasDatabaseRenderingHeight(
          state.model,
          bound,
          boundedHeight
        );
      }
      const hasStableLivePresentationHeight =
        !isLivePresentation || liveIntrinsicHeight > 0;
      if (hasStableLivePresentationHeight) {
        this.setCachedCanvasDatabaseRenderingHeight(
          state.model,
          presentationKey,
          boundedHeight
        );
      }
      if (liveView && liveIntrinsicHeight > 0 && contentHeight > 0) {
        this.setCachedLivePresentationContentHeight(liveView, contentHeight);
      }
      if (setupViewportAlignmentPending) {
        setupViewportAlignmentPending = false;
        this.alignCanvasDatabaseAfterSetupTransition();
      }
    };
    const queueHeightUpdate = () => {
      if (
        heightUpdateFrame != null ||
        !this.isConnected ||
        this.transformState$.value !== 'active'
      ) {
        return;
      }
      heightUpdateFrame = requestAnimationFrame(updateHeight);
    };
    this._queueCanvasHeightUpdate = queueHeightUpdate;
    this._disposables.add(() => {
      this._canvasHeightAutoSizingSetup = false;
      this._queueCanvasHeightUpdate = null;
      this._syncCanvasHeightObserverTargets = null;
      if (heightUpdateFrame != null) {
        cancelAnimationFrame(heightUpdateFrame);
        heightUpdateFrame = null;
      }
      this.setAutoMeasuredTransientXYWH(null);
    });

    // Initial update after render
    queueHeightUpdate();
    const onLiveViewIntrinsicHeightChange = () => {
      queueHeightUpdate();
    };
    const onLiveViewSetupStateChange = () => {
      setupViewportAlignmentPending = true;
      this.focusCanvasDatabaseForSetupTransition();
      this.resetLivePresentationHeightStabilizer();
      this.allowLivePresentationShrinkOnceForCurrentPresentation();
      this.requestUpdate();
      queueHeightUpdate();
    };
    container.addEventListener(
      DATABASE_LIVE_VIEW_INTRINSIC_HEIGHT_CHANGE_EVENT,
      onLiveViewIntrinsicHeightChange
    );
    container.addEventListener(
      DATABASE_LIVE_VIEW_SETUP_STATE_CHANGE_EVENT,
      onLiveViewSetupStateChange
    );
    this._disposables.add(() => {
      container.removeEventListener(
        DATABASE_LIVE_VIEW_INTRINSIC_HEIGHT_CHANGE_EVENT,
        onLiveViewIntrinsicHeightChange
      );
      container.removeEventListener(
        DATABASE_LIVE_VIEW_SETUP_STATE_CHANGE_EVENT,
        onLiveViewSetupStateChange
      );
    });

    // Watch frame slots instead of subtree mutations; live database views can
    // virtualize aggressively and should not wake the canvas on every row patch.
    const observedHeightTargets = new Set<HTMLElement>();
    const observedTargetHeights = new WeakMap<HTMLElement, number>();
    const observer = new ResizeObserver(entries => {
      if (this.transformState$.value !== 'active') {
        return;
      }
      syncHeightObserverTargets();
      const hasMaterialHeightChange = entries.some(entry => {
        const target = entry.target;
        if (!(target instanceof HTMLElement)) {
          return false;
        }
        const nextHeight =
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        const previousHeight = observedTargetHeights.get(target);
        observedTargetHeights.set(target, nextHeight);
        return (
          previousHeight === undefined ||
          Math.abs(nextHeight - previousHeight) > CANVAS_HEIGHT_CHANGE_THRESHOLD
        );
      });
      if (hasMaterialHeightChange) {
        queueHeightUpdate();
      }
    });
    const syncHeightObserverTargets = () => {
      if (this.transformState$.value !== 'active') {
        return;
      }
      for (const target of this.collectLivePresentationResizeTargets(
        container
      )) {
        if (observedHeightTargets.has(target)) {
          continue;
        }
        observedHeightTargets.add(target);
        observer.observe(target);
      }
    };
    this._syncCanvasHeightObserverTargets = syncHeightObserverTargets;
    syncHeightObserverTargets();
    this._disposables.add(() => {
      observer.disconnect();
    });

    // Native drag/drop for external files (e.g. from OS Finder)
    const dbContainer = this.renderRoot.querySelector(
      '[data-testid="edgeless-database-container"]'
    );
    if (dbContainer) {
      const onDragOver = (e: Event) => {
        const de = e as DragEvent;
        if (!de.dataTransfer?.types.includes('Files')) return;
        de.preventDefault();
        de.stopPropagation();
        de.dataTransfer.dropEffect = 'copy';
        this._setDropHighlight(true);
      };

      const onDragLeave = (e: Event) => {
        const de = e as DragEvent;
        const related = de.relatedTarget as Node | null;
        if (related && dbContainer.contains(related)) return;
        this._setDropHighlight(false);
      };

      const onDrop = (e: Event) => {
        const de = e as DragEvent;
        de.preventDefault();
        de.stopPropagation();
        this._setDropHighlight(false);
        const files = de.dataTransfer?.files;
        if (!files || files.length === 0) return;
        this._importFiles([...files]).catch(error =>
          this._handleImportFilesError(error)
        );
      };

      dbContainer.addEventListener('dragover', onDragOver);
      dbContainer.addEventListener('dragleave', onDragLeave);
      dbContainer.addEventListener('drop', onDrop);
      this._disposables.add(() => {
        dbContainer.removeEventListener('dragover', onDragOver);
        dbContainer.removeEventListener('dragleave', onDragLeave);
        dbContainer.removeEventListener('drop', onDrop);
      });
    }
  }

  override getRenderingRect() {
    const state = this.getCanvasDatabaseRuntimeState();

    if (!state || isHiddenSystemManagedDatabaseBlock(state.model)) {
      return {
        h: 0,
        w: 0,
        x: 0,
        y: 0,
        zIndex: this.toZIndex(),
      };
    }

    const bound = this.getCanvasDatabaseBound(state.model);
    const height = this.getCanvasDatabaseRenderingHeight(state.model, bound);

    return {
      x: bound.x,
      y: bound.y,
      w: bound.w,
      h: height,
      zIndex: this.toZIndex(),
    };
  }

  private getCanvasDatabaseRenderingHeight(
    model: DatabaseBlockModel,
    bound: Bound
  ): number {
    if (!this.isCanvasDatabaseStorageReady(model)) {
      return this.toBoundedCanvasHeight(bound.h);
    }

    const cachedHeight = this.getCachedCanvasDatabaseRenderingHeight(model);
    if (cachedHeight > 0) {
      return this.toBoundedCanvasHeight(cachedHeight);
    }
    return this.toBoundedCanvasHeight(bound.h);
  }

  private syncCanvasDatabaseBoundWithActivePresentationHeight() {
    const state = this.getCanvasDatabaseRuntimeState();
    if (
      !state ||
      isHiddenSystemManagedDatabaseBlock(state.model) ||
      !this.isCanvasDatabasePresentationWorkActive(state.model)
    ) {
      return;
    }

    const presentation = this.resolveCanvasPresentation();
    const presentationKey = this.getCanvasPresentationStateKey(
      presentation.state
    );
    const cachedCanvasHeight = this.getCachedCanvasDatabaseRenderingHeight(
      state.model,
      presentationKey
    );
    if (cachedCanvasHeight <= 0) {
      return;
    }
    const targetHeight = this.toBoundedCanvasHeight(cachedCanvasHeight);

    const bound = this.getCanvasDatabaseBound(state.model);
    if (Math.abs(bound.h - targetHeight) <= 1) {
      return;
    }

    this.applyCanvasDatabaseRenderingHeight(state.model, bound, targetHeight);
  }

  override getCSSTransform() {
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state) return '';
    const viewport = state.gfx.viewport;
    const { translateX, translateY, zoom } = viewport;
    const bound = this.getCanvasDatabaseBound(state.model);

    const scaledX = bound.x * zoom;
    const scaledY = bound.y * zoom;
    const deltaX = scaledX - bound.x;
    const deltaY = scaledY - bound.y;

    return `translate(${translateX + deltaX}px, ${translateY + deltaY}px) scale(${zoom})`;
  }

  override renderGfxBlock() {
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state || isHiddenSystemManagedDatabaseBlock(state.model)) {
      return nothing;
    }

    const style = {
      borderRadius: '12px',
    };
    const storageReady = this.isCanvasDatabaseStorageReady(state.model);
    const isLivePresentation =
      storageReady && this.getCanvasPresentationState().mode === 'live';

    return html`
      <div
        class=${classMap({
          [styles.edgelessDatabaseContainer]: true,
          [styles.edgelessDatabaseCanvasToolPassthrough]:
            this.isCanvasToolCapturingPresentation,
        })}
        style=${styleMap(style)}
        data-canvas-db-zone="select-move"
        data-canvas-presentation-interactive=${this
          .isCanvasPresentationInteractive}
        data-canvas-selected=${this._selected}
        data-canvas-selection-editing=${this._editing}
        data-canvas-height-capped=${this._isHeightCapped}
        data-editing=${this._editing}
        data-testid="edgeless-database-container"
      >
        <edgeless-database-background
          .editing=${this._editing}
          .database=${state.model}
        ></edgeless-database-background>
        <div class=${styles.dropTargetOverlay}></div>

        <div
          data-testid="edgeless-database-clip-container"
          @wheel=${this.stopCanvasWheelInInternalWindow}
          class=${classMap({
            [styles.clipContainer]: true,
            [styles.clipContainerScrollable]: !isLivePresentation,
          })}
        >
          <div
            class=${classMap({
              'edgeless-database-content': true,
              [styles.contentContainer]: true,
              [styles.contentContainerViewportBounded]: isLivePresentation,
            })}
          >
            ${this.renderPageContent()}
          </div>
        </div>
        <edgeless-database-mask
          .model=${state.model}
          .host=${this.host}
          .zoom=${state.gfx.viewport.zoom ?? 1}
          .disableMask=${this.isCanvasToolCapturingPresentation}
          .editing=${this._editing}
          .passthrough=${this.isCanvasPresentationInteractive}
        ></edgeless-database-mask>
      </div>
    `;
  }

  @state()
  private accessor _selected = false;

  @state()
  private accessor _editing = false;

  @state()
  private accessor _rowCount = 0;

  @state()
  private accessor _columnCount = 0;

  @state()
  private accessor _isEditingTitle = false;

  @state()
  private accessor _titleDraft = '';

  private _isHeightCapped = false;

  @state()
  private accessor _presentationViewportRestored = true;

  private _presentationScrollHost: HTMLElement | null = null;

  private _canvasDataSourceSubscriptionsSetup = false;

  private _canvasToolSubscriptionSetup = false;

  private _canvasToolSubscriptionAttempts = 0;

  private _canvasDatabaseStorageReadyCleanup: (() => void) | null = null;

  private _markedCanvasDatabaseStorageReady = false;

  private _markedCanvasDatabaseFirstLoadingRender = false;

  private _markedCanvasDatabaseFirstLiveRender = false;

  private _canvasUiAwarenessSetup = false;

  private _presentationScrollTimer: ReturnType<typeof setTimeout> | null = null;

  private _canvasHeaderPointerDown: {
    clientX: number;
    clientY: number;
  } | null = null;

  private _lastPresentationScrollTarget = PRESENTATION_ROOT_SCROLL_TARGET;

  private _pendingPresentationViewId: string | null = null;

  private _pendingPresentationViewportTargets: Record<
    string,
    CanvasDatabasePresentationViewportTarget
  > = {};

  private readonly _presentationScrollListeners = new Map<
    HTMLElement,
    () => void
  >();

  private readonly _presentationScrollTargets = new Map<string, HTMLElement>();

  private readonly _suppressedPresentationScrollTargets = new Set<string>();

  private readonly _suppressedPresentationScrollTargetTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  private readonly _presentationViewportParticipants = new Map<
    string,
    CanvasViewportParticipant
  >();

  private _isApplyingPresentationViewport = false;

  private _lastLocalPresentationViewport: {
    updatedAt: number;
    viewport: CanvasDatabasePresentationViewportTarget & {
      target?: string;
      targets?: Record<string, CanvasDatabasePresentationViewportTarget>;
    };
  } | null = null;

  private _presentationParticipantReadyCleanup: (() => void) | null = null;

  private _presentationInteractionCleanup: (() => void) | null = null;

  private _presentationTargetResizeObserver: ResizeObserver | null = null;

  private _presentationViewportRestoreReadyTimer: ReturnType<
    typeof setTimeout
  > | null = null;

  private _presentationViewportRestoreMaxTimer: ReturnType<
    typeof setTimeout
  > | null = null;

  private _lastAppliedCanvasUiUpdatedAt = 0;

  private _lastLocalCanvasUiUpdatedAt = 0;

  private _lastCanvasPresentationKey: string | null = null;

  private _allowLivePresentationShrinkForKey: string | null = null;

  private _lastLivePresentationRowCount: number | null = null;

  private _lastLivePresentationContentHeight = 0;

  private _livePresentationBodyContractViolationCandidateCount = 0;

  private _livePresentationBodyContractViolationCandidateSignature:
    | string
    | null = null;

  private _livePresentationBodyContractViolationSignature: string | null = null;

  private readonly canvasLivePresentationState$ =
    signal<CanvasDatabasePresentationState | null>(null);

  private _presentationTargetSyncFrame: ReturnType<
    typeof requestAnimationFrame
  > | null = null;

  private readonly canvasHeaderWidget: DataViewWidget = defineUniComponent(
    (props: DataViewWidgetProps) =>
      this.renderCanvasPresentationHeader(
        {
          id: props.dataViewLogic.view.id,
          mode: props.dataViewLogic.view.type,
          name: '',
        },
        {
          showDataViewToolbar: true,
          showViewSwitcher: false,
        }
      )
  );

  private readonly canvasFooterWidget: DataViewWidget = defineUniComponent(
    (props: DataViewWidgetProps) => {
      if (props.dataViewLogic.isSetupWizardOpen()) {
        return html``;
      }
      return this.renderRowDataWindowFooter(props.dataViewLogic.view, {
        surface: 'canvas',
      });
    }
  );

  private renderCanvasDataViewToolbar(
    props: DataViewWidgetProps,
    options: {
      showTools?: boolean;
      summaryActive: boolean;
      titleRow?: TemplateResult;
      trailingActions?: TemplateResult;
    }
  ) {
    const showTools = options.showTools ?? !options.summaryActive;

    return renderDatabaseDataViewChrome({
      props,
      surface: 'canvas',
      titleRow: options.titleRow ?? nothing,
      trailingActions: options.trailingActions ?? nothing,
      showQuickSettings: false,
      toolbarClass: styles.presentationDataViewToolbar,
      tools: showTools ? renderUniLit(this.toolsWidget, props) : nothing,
      viewBarClass: styles.presentationDataViewBar,
      viewBarOptions: {
        activateCurrentViewOnClick: options.summaryActive,
        compactLeadingItem: {
          label: dataViewI18nT('canvas.summary'),
          isSelected: options.summaryActive,
          select: () => this.switchToSummaryPresentation(),
        },
        onChangeView: id => {
          this.setLiveCanvasPresentation(id);
        },
        openOptionsOnActiveViewClick: false,
        selectedViewId: options.summaryActive ? null : undefined,
      },
      wrapContainer: false,
    });
  }

  private renderCanvasDataViewToolbarFromRoot(options: {
    showTools?: boolean;
    summaryActive: boolean;
    titleRow?: TemplateResult;
    trailingActions?: TemplateResult;
  }) {
    const dataViewLogic = this.canvasDataViewRootLogic.value.currentView$.value;
    if (!dataViewLogic) {
      return nothing;
    }
    return this.renderCanvasDataViewToolbar({ dataViewLogic }, options);
  }

  private readonly canvasVirtualPadding$ = signal(0);

  protected override readonly dataSource = lazy(() => {
    const dataSource = new CanvasPresentationDatabaseBlockDataSource(
      this.model,
      dataSource => {
        dataSource.serviceSet(EditorHostKey, this.host);
        this.std.provider
          .getAll(ExternalGroupByConfigProvider)
          .forEach(config => {
            dataSource.serviceSet(
              ExternalGroupByConfigProvider(config.name),
              config
            );
          });
      },
      () => this.publishCanvasUiAwareness()
    );
    const state = this.getCanvasPresentationStateFromStorage(dataSource);
    if (state.mode === 'live' && state.viewId) {
      dataSource.setTransientCurrentView(state.viewId);
    }
    return dataSource;
  });

  private readonly canvasDataSource = lazy(() => this.dataSource.value);

  protected override disposeDataSource() {
    this._canvasDataSourceSubscriptionsSetup = false;
    this.canvasDataViewRootLogic.clear();
    this.canvasDataSource.clear();
    super.disposeDataSource();
  }

  private get canvasPresentationViews(): readonly ViewBasicDataType[] {
    return this.canvasDataSource.value.viewDataList$.value;
  }

  private get canvasPresentationModel() {
    return this.canvasDataSource.value.storageModel;
  }

  private get canvasTitleText() {
    return this.canvasDataSource.value.storageModel.props.title;
  }

  private get canvasTitleValue() {
    return this.canvasTitleText.toString();
  }

  private getCanvasPresentationState() {
    return (
      this.canvasLivePresentationState$.value ??
      this.getCanvasPresentationStateFromStorage(this.canvasDataSource.value)
    );
  }

  private getCanvasPresentationStateFromStorage(
    dataSource: CanvasPresentationDatabaseBlockDataSource
  ) {
    return getCanvasPresentationState(
      dataSource.storageModel,
      dataSource.viewDataList$.value
    );
  }

  private get canvasUiAwarenessScope() {
    return `database:${this.store.id}:${this.blockId}:canvas-ui`;
  }

  private getCanvasPresentationStateKey(
    state: CanvasDatabasePresentationState
  ) {
    if (state.mode === 'live') {
      return `live:${state.viewId ?? ''}`;
    }
    return state.mode;
  }

  private resetLivePresentationHeightStabilizer() {
    this._lastLivePresentationRowCount = null;
    this._lastLivePresentationContentHeight = 0;
  }

  private allowLivePresentationShrinkOnceForCurrentPresentation() {
    const state = this.getCanvasPresentationState();
    if (state.mode !== 'live') {
      return;
    }
    this._allowLivePresentationShrinkForKey =
      this.getCanvasPresentationStateKey(state);
  }

  private markCanvasPresentationKey(state: CanvasDatabasePresentationState) {
    const key = this.getCanvasPresentationStateKey(state);
    const previous = this._lastCanvasPresentationKey;
    this._lastCanvasPresentationKey = key;

    if (previous === null || previous === key) {
      return false;
    }

    this.pruneDeletedLiveCanvasPresentationLayoutState(previous);
    this.resetLivePresentationHeightStabilizer();
    this._allowLivePresentationShrinkForKey =
      state.mode === 'live' ? key : null;
    return true;
  }

  private removeCanvasPresentationLayoutState(presentationKey: string) {
    removeDatabaseCanvasLayoutState(
      this.store.id,
      this.blockId,
      presentationKey
    );
  }

  private pruneDeletedLiveCanvasPresentationLayoutState(
    presentationKey: string
  ) {
    if (!presentationKey.startsWith('live:')) {
      return;
    }
    const viewId = presentationKey.slice('live:'.length);
    if (!viewId) {
      return;
    }
    if (this.canvasPresentationViews.some(view => view.id === viewId)) {
      return;
    }
    this.removeCanvasPresentationLayoutState(presentationKey);
  }

  private repairStaleCanvasPresentationState(): boolean {
    const state = this.getCanvasPresentationState();
    const resolution = resolveCanvasPresentation(
      this.canvasPresentationModel,
      this.canvasPresentationViews,
      state
    );
    if (
      resolution.kind !== 'invalid' ||
      resolution.reason !== 'Canvas presentation view no longer exists.'
    ) {
      return false;
    }

    const stateKey = this.getCanvasPresentationStateKey(state);
    const durableState = this.getCanvasPresentationStateFromStorage(
      this.canvasDataSource.value
    );

    if (durableState !== state) {
      const durableResolution = resolveCanvasPresentation(
        this.canvasPresentationModel,
        this.canvasPresentationViews,
        durableState
      );
      if (durableResolution.kind !== 'invalid') {
        this.removeCanvasPresentationLayoutState(stateKey);
        this.setCanvasUiPresentation(durableState);
        return true;
      }
    }

    const repaired = getRepairedCanvasPresentationState(
      this.canvasPresentationModel,
      this.canvasPresentationViews,
      state
    );
    if (!repaired) {
      return false;
    }

    this.removeCanvasPresentationLayoutState(stateKey);
    this.setCanvasUiPresentation(repaired);
    return true;
  }

  private consumeLivePresentationShrinkAllowance(presentationKey: string) {
    if (this._allowLivePresentationShrinkForKey !== presentationKey) {
      return false;
    }
    this._allowLivePresentationShrinkForKey = null;
    return true;
  }

  private ensureCanvasUiAwareness() {
    if (this._canvasUiAwarenessSetup || !this.isConnected) {
      return;
    }

    this._canvasUiAwarenessSetup = true;
    const updateRemoteCanvasUiState = () => this.updateRemoteCanvasUiState();
    const awareness = this.std.store.awarenessStore.awareness;
    awareness.on('change', updateRemoteCanvasUiState);
    this._disposables.add(() => {
      awareness.off('change', updateRemoteCanvasUiState);
      this.clearLocalCanvasUiAwareness();
    });
    updateRemoteCanvasUiState();
  }

  private publishCanvasUiAwareness(state = this.getCanvasPresentationState()) {
    if (!this.isConnected) {
      return;
    }
    const updatedAt = Math.max(
      Date.now(),
      this._lastLocalCanvasUiUpdatedAt + 1
    );
    const presentation: CanvasDatabasePresentationState = {
      ...state,
      updatedAt,
    };
    this.canvasLivePresentationState$.value = presentation;
    this._lastLocalCanvasUiUpdatedAt = updatedAt;
    const canvasUiState: CanvasUiAwarenessState = {
      scope: this.canvasUiAwarenessScope,
      databaseId: this.blockId,
      presentation,
      searches: this.canvasDataSource.value.getCanvasViewSearchStates(),
      windows: this.canvasDataSource.value.getCanvasViewWindowStates(),
      updatedAt,
    };
    this.std.store.awarenessStore.setLocalStateField(
      'databaseCanvasUi',
      canvasUiState
    );
  }

  private clearLocalCanvasUiAwareness() {
    const current =
      this.std.store.awarenessStore.getLocalState()?.databaseCanvasUi;
    if (
      current?.scope !== this.canvasUiAwarenessScope ||
      current.databaseId !== this.blockId
    ) {
      return;
    }
    this.std.store.awarenessStore.setLocalStateField(
      'databaseCanvasUi',
      undefined
    );
  }

  private updateRemoteCanvasUiState() {
    const awarenessStore = this.std.store.awarenessStore;
    const localClientId = awarenessStore.awareness.clientID;
    let next:
      | {
          presentation: CanvasDatabasePresentationState;
          searches?: Record<string, CanvasViewSearchState>;
          windows?: Record<string, CanvasViewWindowState>;
          updatedAt: number;
        }
      | undefined;

    awarenessStore.getStates().forEach((state, clientId) => {
      if (clientId === localClientId) {
        return;
      }
      const canvasUi = state.databaseCanvasUi;
      if (
        !canvasUi ||
        canvasUi.scope !== this.canvasUiAwarenessScope ||
        canvasUi.databaseId !== this.blockId
      ) {
        return;
      }
      if (!next || canvasUi.updatedAt > next.updatedAt) {
        next = {
          presentation:
            canvasUi.presentation as CanvasDatabasePresentationState,
          searches: canvasUi.searches,
          updatedAt: canvasUi.updatedAt,
          windows: canvasUi.windows,
        };
      }
    });

    if (
      !next ||
      next.updatedAt <= this._lastAppliedCanvasUiUpdatedAt ||
      next.updatedAt <= this._lastLocalCanvasUiUpdatedAt
    ) {
      return;
    }

    this._lastAppliedCanvasUiUpdatedAt = next.updatedAt;
    this.markCanvasPresentationKey(this.getCanvasPresentationState());
    const presentationChanged = this.markCanvasPresentationKey(
      next.presentation
    );
    this.canvasLivePresentationState$.value = next.presentation;
    this.canvasDataSource.value.setCanvasViewSearchStates(next.searches ?? {});
    this.canvasDataSource.value.setCanvasViewWindowStates(next.windows ?? {});
    if (next.presentation.mode === 'live') {
      this.canvasDataSource.value.setTransientCurrentView(
        next.presentation.viewId
      );
    } else {
      this.canvasDataSource.value.setTransientCurrentView(undefined);
    }
    if (presentationChanged) {
      this.resetHeightCap();
    }
    this.updateSummaryCounts();
    this.requestUpdate();
    this.queuePresentationScrollTargetSync();
  }

  private resolveCanvasPresentation() {
    return resolveCanvasPresentation(
      this.canvasPresentationModel,
      this.canvasPresentationViews,
      this.getCanvasPresentationState()
    );
  }

  private readonly canvasViewSelection$ = computed(() => {
    const databaseSelection = this.selection.value.find(
      (selection): selection is DatabaseSelection => {
        if (selection.blockId !== this.blockId) {
          return false;
        }
        return selection instanceof DatabaseSelection;
      }
    );
    return databaseSelection?.viewSelection;
  });

  protected override get rowFocusSelection$() {
    return this.canvasViewSelection$;
  }

  protected override get rowFocusDataSource() {
    return this.canvasDataSource.value;
  }

  protected override get databaseRowFocusScope() {
    return `database:${this.store.id}:${this.blockId}:canvas`;
  }

  protected override shouldForceLargeDataWindowControls() {
    return true;
  }

  private readonly setCanvasSelection = (
    selection: DataViewSelection | undefined
  ) => {
    if (selection) {
      getSelection()?.removeAllRanges();
    }
    this.selection.setGroup(
      'note',
      selection
        ? [
            new DatabaseSelection({
              blockId: this.blockId,
              viewSelection: selection,
            }),
          ]
        : []
    );
  };

  private readonly canvasDataViewRootLogic = lazy(
    () =>
      new DataViewRootUILogic({
        performanceScope: this.std,
        virtualPadding$: this.canvasVirtualPadding$,
        bindHotkey: hotkeys => {
          return {
            dispose: this.host.event.bindHotkey(hotkeys, {
              blockId: this.topContenteditableElement?.blockId ?? this.blockId,
            }),
          };
        },
        handleEvent: (name, handler) => {
          return {
            dispose: this.host.event.add(name, handler, {
              blockId: this.blockId,
            }),
          };
        },
        selection$: this.canvasViewSelection$,
        remoteAreaSelections$: this.remoteAreaSelections$,
        remoteRowFocuses$: this.remoteRowFocuses$,
        remoteCellPresences$: this.remoteCellPresences$,
        setSelection: this.setCanvasSelection,
        preferDesktopRenderer: true,
        dataSource: this.canvasDataSource.value,
        headerWidget: this.canvasHeaderWidget,
        footerWidget: this.canvasFooterWidget,
        onDrag: this.onDrag,
        clipboard: this.std.clipboard,
        notification: {
          toast: message => {
            const notification = this.std.getOptional(NotificationProvider);
            if (notification) {
              notification.toast(message);
            } else {
              toast(this.host, message);
            }
          },
        },
        eventTrace: (key, params) => {
          const telemetryService = this.std.getOptional(TelemetryProvider);
          telemetryService?.track(key, {
            ...(params as TelemetryEventMap[typeof key]),
            blockId: this.blockId,
          });
        },
        detailPanelConfig: {
          openDetailPanel: async (
            _target: HTMLElement,
            data: {
              view: SingleView;
              rowId: string;
            }
          ) => {
            return popSideDetail(
              this.createTemplate(data, () => {
                //
              })
            );
          },
        },
      })
  );

  protected override requestDataViewFrameUpdate() {
    this.canvasDataViewRootLogic.value.dataViewRenderer?.requestUpdate();
  }

  private get isCanvasPresentationInteractive(): boolean {
    if (this.isCanvasToolCapturingPresentation) {
      return false;
    }
    return resolveCanvasDatabasePresentationInteractivity({
      editing: this._editing,
      selected: this._selected,
      selectedCount: this.gfx.selection.selectedIds.length,
    });
  }

  private get isCanvasToolCapturingPresentation(): boolean {
    return shouldRouteCanvasDatabasePointerToCanvasTool(
      this.gfx?.tool?.currentToolName$?.value
    );
  }

  private subscribeCanvasToolState(): void {
    if (this._canvasToolSubscriptionSetup) {
      return;
    }
    const toolName$ = this.gfx?.tool?.currentToolName$;
    if (!toolName$) {
      if (this._canvasToolSubscriptionAttempts >= 3) {
        return;
      }
      this._canvasToolSubscriptionAttempts += 1;
      requestAnimationFrame(() => {
        if (this.isConnected) {
          this.subscribeCanvasToolState();
        }
      });
      return;
    }
    this._canvasToolSubscriptionSetup = true;
    this._disposables.add(
      toolName$.subscribe(() => {
        this.requestUpdate();
      })
    );
  }

  private isCanvasDatabaseStorageReady(model?: DatabaseBlockModel | null) {
    const targetModel = model ?? this.getCanvasDatabaseRuntimeState()?.model;
    if (!targetModel) return false;

    if (isDatabaseStorageModelReady(targetModel)) {
      this.markCanvasDatabaseLoadOnce('storage-ready', {
        source: 'sync-check',
      });
      this.teardownCanvasDatabaseStorageReadyObserver();
      return true;
    }

    this.observeCanvasDatabaseStorageReady(targetModel);
    return false;
  }

  private isCanvasDatabasePresentationWorkActive(
    model?: DatabaseBlockModel | null
  ) {
    return shouldRunCanvasDatabasePresentationWork({
      storageReady: this.isCanvasDatabaseStorageReady(model),
      transformState: this.transformState$.value,
    });
  }

  private observeCanvasDatabaseStorageReady(model: DatabaseBlockModel) {
    if (this._canvasDatabaseStorageReadyCleanup) {
      return;
    }

    this._canvasDatabaseStorageReadyCleanup = observeDatabaseStorageModelReady(
      model,
      () => {
        this.teardownCanvasDatabaseStorageReadyObserver();
        this.markCanvasDatabaseLoadOnce('storage-ready', {
          source: 'observer',
        });
        this.ensureCanvasDataSourceSubscriptions();
        this.requestUpdate();
        this.queuePostUpdateCanvasSync('storage-ready');
        this._queueCanvasHeightUpdate?.();
      }
    );
  }

  private teardownCanvasDatabaseStorageReadyObserver() {
    this._canvasDatabaseStorageReadyCleanup?.();
    this._canvasDatabaseStorageReadyCleanup = null;
  }

  private ensureCanvasDataSourceSubscriptions() {
    if (
      this._canvasDataSourceSubscriptionsSetup ||
      !this.isConnected ||
      !this.isCanvasDatabaseStorageReady()
    ) {
      return;
    }

    this._canvasDataSourceSubscriptionsSetup = true;
    this.updateSummaryCounts();
    this.ensureCanvasUiAwareness();
    const dataSource = this.canvasDataSource.value;
    const titleText = dataSource.storageModel.props.title;
    const titleUpdate = () => this.requestUpdate();
    if (titleText?.yText) {
      titleText.yText.observe(titleUpdate);
      this._disposables.add(() => {
        titleText.yText.unobserve(titleUpdate);
      });
    }
    this._disposables.add(
      dataSource.rowCount$.subscribe(() => {
        this.resetHeightCap();
        this.updateSummaryCounts();
        this.requestUpdate();
        this.queuePostUpdateCanvasSync('row-count');
      })
    );
    this._disposables.add(
      dataSource.columnCount$.subscribe(() => {
        this.resetHeightCap();
        this.updateSummaryCounts();
      })
    );
    this._disposables.add(
      dataSource.storageModel.propsUpdated.subscribe(({ key }) => {
        if (key === 'canvasPresentation') {
          const presentationChanged =
            this.applyCanvasPresentationStateFromStorage();
          if (presentationChanged) {
            this.resetHeightCap();
          }
          this.updateSummaryCounts();
          this.requestUpdate();
          this.queuePresentationScrollTargetSync();
          return;
        }
        if (
          key === 'activeViewId' ||
          key === 'columns' ||
          key === 'databaseShardManifest' ||
          key === 'title' ||
          key === 'views'
        ) {
          this.resetHeightCap();
          this.updateSummaryCounts();
          this.requestUpdate();
        }
      })
    );
  }

  private getSummaryColumnIds(): string[] {
    if (!this.isCanvasDatabaseStorageReady()) {
      return [];
    }
    const dataSource = this.canvasDataSource.value;
    const propertyIds = dataSource.getAllPropertyIds();
    const summaryIds = [...propertyIds];
    const seen = new Set<string>();
    return summaryIds.filter(propertyId => {
      if (seen.has(propertyId)) {
        return false;
      }
      seen.add(propertyId);
      const propertyType = dataSource.propertyTypeGet(propertyId) ?? propertyId;
      return !SUMMARY_EXCLUDED_PROPERTY_TYPES.has(propertyType);
    });
  }

  private markCanvasDatabaseLoadOnce(
    event: 'first-live-render' | 'first-loading-render' | 'storage-ready',
    data: Record<string, unknown> = {}
  ) {
    if (event === 'storage-ready' && this._markedCanvasDatabaseStorageReady) {
      return;
    }
    if (
      event === 'first-loading-render' &&
      this._markedCanvasDatabaseFirstLoadingRender
    ) {
      return;
    }
    if (
      event === 'first-live-render' &&
      this._markedCanvasDatabaseFirstLiveRender
    ) {
      return;
    }

    if (event === 'storage-ready') {
      this._markedCanvasDatabaseStorageReady = true;
    } else if (event === 'first-loading-render') {
      this._markedCanvasDatabaseFirstLoadingRender = true;
    } else {
      this._markedCanvasDatabaseFirstLiveRender = true;
    }

    const state = this.getCanvasDatabaseRuntimeState();
    markCanvasDatabaseLoad(event, {
      blockId: this.blockId,
      docId: this.store?.id,
      modelId: state?.model.id,
      ...data,
    });
  }

  private getSummaryRowCount(): number {
    if (!this.isCanvasDatabaseStorageReady()) {
      return 0;
    }
    const manifest =
      this.canvasDataSource.value.storageModel.props.databaseShardManifest;
    if (manifest) {
      return manifest.shards.reduce((sum, shard) => sum + shard.rowCount, 0);
    }
    return this.canvasDataSource.value.rowCount$.value;
  }

  private updateSummaryCounts() {
    this._rowCount = this.getSummaryRowCount();
    this._columnCount = this.getSummaryColumnIds().length;
  }

  private toBoundedCanvasHeight(height: number) {
    return Math.min(
      MAX_CANVAS_DATABASE_HEIGHT,
      Math.max(MIN_CANVAS_DATABASE_HEIGHT, height)
    );
  }

  private estimateLivePresentationContentHeight() {
    if (!this.isCanvasDatabaseStorageReady()) {
      return 0;
    }
    if (this.resolveCanvasPresentation().kind !== 'live') {
      return 0;
    }

    const rowCount = this.canvasDataSource.value.rowCount$.value;

    return (
      CANVAS_DATABASE_LIVE_CHROME_HEIGHT +
      rowCount * CANVAS_DATABASE_LIVE_ROW_HEIGHT +
      CANVAS_DATABASE_VERTICAL_PADDING
    );
  }

  private getCachedCanvasDatabaseRenderingHeight(
    model: DatabaseBlockModel,
    presentationKey?: string
  ) {
    if (!presentationKey) {
      if (!this.isCanvasDatabaseStorageReady(model)) {
        return 0;
      }
      presentationKey = this.getCanvasPresentationStateKey(
        this.getCanvasPresentationState()
      );
    }
    return (
      readDatabaseCanvasLayoutState(model.store.id, model.id, presentationKey)
        ?.height ?? 0
    );
  }

  private setCachedCanvasDatabaseRenderingHeight(
    model: DatabaseBlockModel,
    presentationKey: string,
    height: number
  ) {
    const boundedHeight = this.toBoundedCanvasHeight(height);
    const previous = this.getCachedCanvasDatabaseRenderingHeight(
      model,
      presentationKey
    );
    if (Math.abs(previous - boundedHeight) <= 1) {
      return;
    }
    writeDatabaseCanvasLayoutState(
      model.store.id,
      model.id,
      presentationKey,
      boundedHeight
    );
  }

  private getCachedLivePresentationContentHeight(view: ViewBasicDataType) {
    return (
      this.canvasDataSource.value.getViewLayoutState(view.id, view.mode)
        ?.intrinsicContentHeight ?? 0
    );
  }

  private setCachedLivePresentationContentHeight(
    view: ViewBasicDataType,
    intrinsicContentHeight: number
  ) {
    const previous = this.getCachedLivePresentationContentHeight(view);
    if (Math.abs(previous - intrinsicContentHeight) <= 1) {
      return;
    }
    this.canvasDataSource.value.setViewLayoutState(view.id, {
      intrinsicContentHeight,
      measuredAt: Date.now(),
      version: 1,
      viewMode: view.mode,
    });
  }

  private applyCanvasDatabaseRenderingHeight(
    _model: DatabaseBlockModel,
    bound: Bound,
    height: number
  ) {
    const normalizedHeight = this.toBoundedCanvasHeight(Math.round(height));
    if (
      Math.abs(bound.h - normalizedHeight) <= CANVAS_HEIGHT_CHANGE_THRESHOLD
    ) {
      return;
    }
    bound.h = normalizedHeight;
    // Intrinsic presentation height is renderer-local state. Persisting an
    // intermediate DOM measurement would add noisy geometry entries to Yjs
    // history and can briefly leave selection overlays on a stale bound.
    this.setAutoMeasuredTransientXYWH(bound.serialize());
  }

  private stabilizeLivePresentationContentHeight(
    liveIntrinsicHeight: number,
    cachedContentHeight: number,
    estimatedContentHeight: number,
    fallbackContentHeight: number,
    currentBoundHeight: number,
    allowShrinkByPresentationChange = false
  ) {
    const rowCount = this.canvasDataSource.value.rowCount$.value;
    if (
      liveIntrinsicHeight <= 0 &&
      this._lastLivePresentationContentHeight > 0
    ) {
      return this._lastLivePresentationContentHeight;
    }
    const canShrinkByIntrinsicHeight =
      liveIntrinsicHeight > 0 || allowShrinkByPresentationChange;
    const measuredContentHeight = canShrinkByIntrinsicHeight
      ? liveIntrinsicHeight || fallbackContentHeight
      : Math.max(
          liveIntrinsicHeight,
          cachedContentHeight,
          estimatedContentHeight
        ) || fallbackContentHeight;
    const previousAcceptedHeight =
      this._lastLivePresentationContentHeight || currentBoundHeight;
    const rowCountDecreased =
      this._lastLivePresentationRowCount !== null &&
      rowCount < this._lastLivePresentationRowCount;
    const shouldAcceptHeight =
      previousAcceptedHeight === 0 ||
      measuredContentHeight > previousAcceptedHeight ||
      rowCountDecreased ||
      canShrinkByIntrinsicHeight;

    this._lastLivePresentationRowCount = rowCount;
    if (shouldAcceptHeight) {
      this._lastLivePresentationContentHeight = measuredContentHeight;
    } else if (this._lastLivePresentationContentHeight === 0) {
      // On reload the table can render before row-height measurements land.
      // Seed from the durable canvas bound so the block never flashes down to
      // a partial/min-height measurement and then expands again a frame later.
      this._lastLivePresentationContentHeight = currentBoundHeight;
    }
    return this._lastLivePresentationContentHeight;
  }

  private measureLivePresentationIntrinsicHeight(
    container: HTMLElement,
    visualScale = 1,
    expectedViewMode?: string
  ) {
    const scale = visualScale || 1;
    const presentation = container.querySelector<HTMLElement>(
      '[data-testid="edgeless-database-presentation"]'
    );
    const liveView = container.querySelector<
      HTMLElement & {
        getIntrinsicContentHeight?: (visualScale?: number) => number;
      }
    >(DATABASE_LIVE_VIEW_SELECTOR);
    const renderedViewMode = liveView
      ? this.getLivePresentationDomViewMode(liveView)
      : undefined;
    if (
      expectedViewMode &&
      (!renderedViewMode || renderedViewMode !== expectedViewMode)
    ) {
      return 0;
    }
    const liveViewHeight = liveView?.getIntrinsicContentHeight?.(scale) ?? 0;
    const liveBody = liveView?.querySelector<HTMLElement>(
      `[${DATA_VIEW_BODY_STATE_ATTRIBUTE}]`
    );
    const liveBodyCheck = checkDataViewBodyContract({
      body: liveBody,
      host: liveView,
      visualScale: scale,
    });
    if (liveBodyCheck.violation) {
      this.reportLivePresentationBodyContractViolation({
        diagnostics: liveBodyCheck.diagnostics,
        expectedViewMode,
        metrics: liveBodyCheck.metrics,
        renderedViewMode,
        violation: liveBodyCheck.violation.reason,
      });
      return 0;
    }
    this.clearLivePresentationBodyContractViolation();
    const uncappedTableBodyHeight =
      renderedViewMode === 'table' && !this._isHeightCapped && liveBody
        ? liveBody.getBoundingClientRect().height / scale
        : 0;
    const frame = getDataViewFrameElement(presentation) ?? presentation;
    const metrics = measureDataViewFrameMetrics({
      // The virtual table's intrinsic rows exclude the body's border and
      // reserved first-row slot. In uncapped mode the body is auto-height, so
      // its border box is content-driven and safe to include without creating
      // a host-height feedback loop.
      bodyIntrinsicHeight: Math.max(liveViewHeight, uncappedTableBodyHeight),
      bodyRenderHeight: liveBodyCheck.metrics.renderHeight,
      frame,
      paddingHeight: CANVAS_DATABASE_VERTICAL_PADDING,
      visualScale: scale,
    });

    if (!metrics.topHeight && !metrics.bodyHeight && !metrics.footerHeight) {
      return 0;
    }

    return metrics.totalHeight;
  }

  private measureSummaryPresentationIntrinsicHeight(container: HTMLElement) {
    const summary = container.querySelector<HTMLElement>(
      '[data-testid="edgeless-database-summary"]'
    );
    if (!summary) {
      return 0;
    }
    const { height } = getGfxBlockElementModelSize(summary);
    return Math.max(height, summary.scrollHeight);
  }

  private reportLivePresentationBodyContractViolation({
    diagnostics,
    expectedViewMode,
    metrics,
    renderedViewMode,
    violation,
  }: {
    diagnostics: ReturnType<typeof checkDataViewBodyContract>['diagnostics'];
    expectedViewMode?: string;
    metrics: ReturnType<typeof checkDataViewBodyContract>['metrics'];
    renderedViewMode?: string;
    violation: string;
  }): void {
    const signature = JSON.stringify({
      expectedViewMode,
      renderedViewMode,
      violation,
    });
    if (
      signature ===
      this._livePresentationBodyContractViolationCandidateSignature
    ) {
      this._livePresentationBodyContractViolationCandidateCount += 1;
    } else {
      this._livePresentationBodyContractViolationCandidateSignature = signature;
      this._livePresentationBodyContractViolationCandidateCount = 1;
    }
    if (
      this._livePresentationBodyContractViolationCandidateCount <
      LIVE_PRESENTATION_BODY_CONTRACT_STABLE_FRAME_THRESHOLD
    ) {
      this._queueCanvasHeightUpdate?.();
      return;
    }
    if (signature === this._livePresentationBodyContractViolationSignature) {
      return;
    }
    this._livePresentationBodyContractViolationSignature = signature;
    console.error('[database-canvas] live data-view body contract violated', {
      diagnostics,
      expectedViewMode,
      metrics,
      renderedViewMode,
      violation,
    });
    this.dispatchEvent(
      new CustomEvent('database-live-view-body-contract-violation', {
        bubbles: true,
        composed: true,
        detail: {
          diagnostics,
          expectedViewMode,
          metrics,
          renderedViewMode,
          violation,
        },
      })
    );
  }

  private clearLivePresentationBodyContractViolation(): void {
    this._livePresentationBodyContractViolationCandidateCount = 0;
    this._livePresentationBodyContractViolationCandidateSignature = null;
    this._livePresentationBodyContractViolationSignature = null;
  }

  private collectLivePresentationResizeTargets(container: HTMLElement) {
    const targets: HTMLElement[] = [];
    const addTarget = (target: HTMLElement | null | undefined) => {
      if (!target || targets.includes(target)) {
        return;
      }
      targets.push(target);
    };
    const frame = container.querySelector<HTMLElement>(
      `[${DATA_VIEW_FRAME_ATTRIBUTE}="true"]`
    );

    addTarget(container);
    addTarget(frame);
    addTarget(
      frame?.querySelector<HTMLElement>(
        `[${DATA_VIEW_FRAME_TOP_ATTRIBUTE}="true"]`
      )
    );
    addTarget(
      frame?.querySelector<HTMLElement>(
        `[${DATA_VIEW_FRAME_BODY_ATTRIBUTE}="true"]`
      )
    );
    addTarget(
      frame?.querySelector<HTMLElement>(
        `[${DATA_VIEW_FRAME_FOOTER_ATTRIBUTE}="true"]`
      )
    );
    addTarget(
      container.querySelector<HTMLElement>(DATABASE_LIVE_VIEW_SELECTOR)
    );
    for (const bodyTarget of container.querySelectorAll<HTMLElement>(
      [
        `[${DATA_VIEW_BODY_INTRINSIC_ATTRIBUTE}="true"]`,
        `[${DATA_VIEW_BODY_RENDER_CONTENT_ATTRIBUTE}="true"]`,
        `[${DATA_VIEW_BODY_RENDER_ROOT_ATTRIBUTE}="true"]`,
      ].join(',')
    )) {
      addTarget(bodyTarget);
    }
    return targets;
  }

  private getLivePresentationDomViewMode(element: HTMLElement) {
    switch (element.tagName.toLowerCase()) {
      case 'dv-calendar-view-ui':
        return 'calendar';
      case 'dv-gantt-view-ui':
        return 'gantt';
      case 'dv-kanban-view-ui':
        return 'kanban';
      case 'dv-table-view-ui-virtual':
        return 'table';
      default:
        return undefined;
    }
  }

  private enforceCanvasHeightBounds() {
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state || !this.isCanvasDatabasePresentationWorkActive(state.model)) {
      return;
    }
    const bound = this.getCanvasDatabaseBound(state.model);
    const boundedHeight = this.toBoundedCanvasHeight(bound.h);

    this.setCanvasHeightCapped(boundedHeight >= MAX_CANVAS_DATABASE_HEIGHT - 1);

    if (Math.abs(bound.h - boundedHeight) <= 1) {
      return;
    }

    this.applyCanvasDatabaseRenderingHeight(state.model, bound, boundedHeight);
  }

  private resetHeightCap() {
    this.setCanvasHeightCapped(false);
  }

  private setCanvasHeightCapped(isHeightCapped: boolean) {
    this._isHeightCapped = isHeightCapped;
    this.syncCanvasHeightCappedAttribute();
  }

  private syncCanvasHeightCappedAttribute() {
    const container = this.renderRoot.querySelector<HTMLElement>(
      '[data-testid="edgeless-database-container"]'
    );
    container?.setAttribute(
      'data-canvas-height-capped',
      String(this._isHeightCapped)
    );
  }

  override renderPageContent() {
    const storageReady = this.isCanvasDatabaseStorageReady();
    const rowReadiness = storageReady
      ? this.canvasDataSource.value.getContentReadiness()
      : { status: 'loading' as const };
    const rowProgress =
      rowReadiness.status === 'error' ? undefined : rowReadiness.progress;
    const resolution =
      storageReady && rowReadiness.status !== 'error'
        ? this.resolveCanvasPresentation()
        : null;
    const readiness = resolveCanvasDatabasePresentationReadiness({
      error: rowReadiness.status === 'error' ? rowReadiness.message : undefined,
      invalidReason: resolution?.kind === 'invalid' ? resolution.reason : '',
      presentationKind: resolution?.kind as
        | CanvasDatabasePresentationKind
        | undefined,
      storageReady,
    });

    if (readiness.status === 'loading') {
      return this.renderLoadingPresentation({
        progress: rowProgress,
        showViewSwitcher: false,
      });
    }

    if (readiness.status === 'unavailable' || readiness.status === 'error') {
      return this.renderInvalidPresentation(readiness.reason, {
        role: rowReadiness.status === 'error' ? 'alert' : undefined,
        shardIntegrityError: rowReadiness.status === 'error',
        showRepairConsequence:
          rowReadiness.status === 'error' && rowReadiness.repairable,
        showRepairAction:
          rowReadiness.status === 'error' &&
          rowReadiness.repairable &&
          !this.canvasDataSource.value.readonly$.value,
        showSummaryButton: rowReadiness.status !== 'error',
        testId:
          rowReadiness.status === 'error'
            ? 'database-row-shard-error'
            : undefined,
      });
    }

    if (!resolution) {
      return this.renderLoadingPresentation({
        showViewSwitcher: false,
      });
    }

    if (resolution.kind === 'live') {
      return this.renderLivePresentation(resolution.view);
    }
    return this.renderSummaryContent();
  }

  private renderCanvasEditableTitle(rawTitle: string, titleText: string) {
    return this._isEditingTitle
      ? html`
          <input
            class=${styles.summaryTitleInput}
            data-canvas-db-zone="content-edit"
            .value=${this._titleDraft}
            .size=${this.titleInputSize}
            @input=${this.onTitleInput}
            @keydown=${this.onTitleKeydown}
            @blur=${this.onTitleBlur}
            @pointerdown=${this.stopTitleEditorPointer}
            placeholder="Untitled"
          />
        `
      : html`
          <div
            class=${classMap({
              [styles.summaryTitle]: true,
              [styles.summaryTitleEmpty]: !rawTitle,
            })}
            data-canvas-db-zone="content-edit"
            title=${titleText}
            @pointerdown=${this.stopTitleEditorPointer}
            @click=${this.onTitleClick}
          >
            ${titleText}
          </div>
        `;
  }

  private renderSummaryContent() {
    const dataSource = this.canvasDataSource.value;
    const rawTitle = this.isCanvasDatabaseStorageReady()
      ? this.canvasTitleValue
      : '';
    const titleText = rawTitle || 'Untitled';
    const summaryColumnIds = this.getSummaryColumnIds();
    const summaryColumns = summaryColumnIds.map(propertyId => {
      const rawName = dataSource.propertyNameGet(propertyId).trim();
      const name = rawName || 'Untitled';
      const type = dataSource.propertyTypeGet(propertyId) ?? 'unknown';
      const meta = dataSource.propertyMetaGet(type);
      return {
        id: propertyId,
        name,
        type,
        icon: meta?.renderer?.icon ?? null,
      };
    });

    return html`
      <div
        class=${styles.summaryContainer}
        data-testid="edgeless-database-summary"
      >
        <div
          class=${styles.summaryHeader}
          @pointerdown=${this.rememberCanvasHeaderPointerDown}
          @click=${this.fitCanvasDatabaseFromHeaderClick}
        >
          ${this.renderCanvasDataViewToolbarFromRoot({
            summaryActive: true,
            titleRow: this.renderCanvasEditableTitle(rawTitle, titleText),
            trailingActions: html`
              ${this.renderDatabaseOps()} ${this.renderFullscreenButton()}
            `,
          })}
        </div>
        <div class=${styles.summaryMeta}>
          <div class=${styles.summaryMetaItem}>
            <div class=${styles.summaryMetaLabel}>Columns</div>
            <div class=${styles.summaryMetaValue}>${this._columnCount}</div>
          </div>
          <div class=${styles.summaryMetaItem}>
            <div class=${styles.summaryMetaLabel}>Rows</div>
            <div class=${styles.summaryMetaValue}>${this._rowCount}</div>
          </div>
        </div>
        <div class=${styles.summaryColumns}>
          <div class=${styles.summaryMetaLabel}>Columns</div>
          ${summaryColumns.length
            ? html`
                <ul class=${styles.summaryColumnsList}>
                  ${summaryColumns.map(
                    column => html`
                      <li class=${styles.summaryColumnsItem}>
                        <span class=${styles.summaryColumnIcon}>
                          ${column.icon ? renderUniLit(column.icon, {}) : null}
                        </span>
                        <span class=${styles.summaryColumnName}>
                          ${column.name}
                        </span>
                        <span
                          class=${styles.summaryTypeChip}
                          style=${styleMap({
                            backgroundColor: getTypeColor(column.type),
                          })}
                        >
                          ${normalizeTypeLabel(column.type)}
                        </span>
                      </li>
                    `
                  )}
                </ul>
              `
            : html`<div class=${styles.summaryEmptyText}>No columns</div>`}
        </div>
      </div>
    `;
  }

  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (
      !shouldScheduleCanvasDatabasePresentationSync(changedProperties.keys())
    ) {
      return;
    }
    this.queuePostUpdateCanvasSync('render-update');
  }

  private queuePostUpdateCanvasSync(reason: CanvasDatabaseSyncReason) {
    this._postUpdateCanvasSyncScheduler.queue(reason);
  }

  private runPostUpdateCanvasSync(
    _reasons: readonly CanvasDatabaseSyncReason[]
  ) {
    this.initializeCanvasDatabaseRuntime();
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state) return;
    this.syncSystemManagedCanvasVisibility();
    if (isHiddenSystemManagedDatabaseBlock(state.model)) {
      return;
    }
    if (!this.isCanvasDatabaseStorageReady(state.model)) {
      return;
    }
    if (!this.isCanvasDatabasePresentationWorkActive(state.model)) {
      return;
    }
    this.repairStaleCanvasPresentationState();
    this.syncLivePresentationCurrentView();
    this.setupCanvasHeightAutoSizing();
    this._syncCanvasHeightObserverTargets?.();
    this._queueCanvasHeightUpdate?.();
    this.syncCanvasDatabaseBoundWithActivePresentationHeight();
    this.enforceCanvasHeightBounds();
    this.applyObservedPresentationViewport();
  }

  private syncSystemManagedCanvasVisibility() {
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state) {
      if (isFileIndexDatabaseId(this.blockId)) {
        this.style.display = 'none';
      }
      return;
    }
    const { gfx, model } = state;
    const hidden = isHiddenSystemManagedDatabaseBlock(model);
    this.style.display = hidden ? 'none' : '';
    if (hidden && gfx.selection.has(model.id)) {
      gfx.selection.set({
        elements: gfx.selection.selectedIds.filter(id => id !== model.id),
        editing: false,
      });
    }
  }

  private renderInvalidPresentation(
    reason: string,
    options: {
      role?: 'alert';
      shardIntegrityError?: boolean;
      showRepairAction?: boolean;
      showRepairConsequence?: boolean;
      showSummaryButton?: boolean;
      showViewSwitcher?: boolean;
      testId?: string;
    } = {}
  ) {
    return html`
      <div
        class=${styles.presentationContainer}
        data-data-view-frame="true"
        data-testid=${options.testId ??
        'edgeless-database-presentation-invalid'}
        role=${options.role ?? nothing}
      >
        ${this.renderCanvasPresentationHeader(undefined, {
          showFullscreenButton: false,
          showViewSwitcher: options.showViewSwitcher ?? true,
        })}
        <div class=${styles.presentationInvalid}>
          <div class=${styles.presentationInvalidTitle}>
            ${options.shardIntegrityError
              ? dataViewI18nT('database.shardRepair.title')
              : dataViewI18nT('canvas.unavailable.title')}
          </div>
          <div class=${styles.presentationInvalidText}>${reason}</div>
          ${options.showRepairConsequence
            ? html`
                <div class=${styles.presentationInvalidText}>
                  ${dataViewI18nT('database.shardRepair.consequence')}
                </div>
                ${options.showRepairAction
                  ? html`
                      <button
                        class=${styles.presentationButton}
                        data-database-repair-action="true"
                        data-testid="database-row-shard-repair"
                        type="button"
                        @click=${this.repairRowShardManifestDrift}
                      >
                        ${dataViewI18nT('database.shardRepair.action')}
                      </button>
                    `
                  : nothing}
              `
            : nothing}
          ${options.showSummaryButton !== false
            ? html`
                <button
                  class=${styles.presentationButton}
                  @click=${this.switchToSummaryPresentation}
                >
                  ${dataViewI18nT('canvas.summary')}
                </button>
              `
            : nothing}
        </div>
      </div>
    `;
  }

  protected override readonly repairRowShardManifestDrift = (
    event: MouseEvent
  ) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      this.canvasDataSource.value.repairLoadedRowShardManifestDrift();
      toast(this.host, dataViewI18nT('database.shardRepair.success'));
      this.requestUpdate();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : dataViewI18nT('database.shardRepair.failed');
      toast(this.host, message);
    }
  };

  private renderLoadingPresentation(
    options: {
      progress?: DatabaseRowShardHydrationProgress;
      showViewSwitcher?: boolean;
    } = {}
  ) {
    this.markCanvasDatabaseLoadOnce('first-loading-render', {
      showViewSwitcher: options.showViewSwitcher ?? false,
    });

    return html`
      <div
        class=${styles.presentationContainer}
        data-data-view-frame="true"
        data-testid="edgeless-database-presentation-loading"
      >
        ${this.renderCanvasPresentationHeader(undefined, {
          showFullscreenButton: false,
          showViewSwitcher: options.showViewSwitcher ?? false,
        })}
        <div class=${styles.presentationLoadingBody} role="status">
          <div class=${styles.presentationInvalidTitle}>
            ${dataViewI18nT('canvas.loading.title')}
          </div>
          <div class=${styles.presentationInvalidText}>
            ${dataViewI18nT('canvas.loading.description')}
          </div>
          <progress
            class=${styles.presentationLoadingProgress}
            aria-label=${dataViewI18nT('canvas.loading.description')}
            max=${options.progress?.totalRows ||
            options.progress?.totalShards ||
            1}
            value=${options.progress
              ? options.progress.totalRows > 0
                ? options.progress.loadedRows
                : options.progress.readyShards
              : nothing}
          ></progress>
          ${options.progress
            ? html`<div class=${styles.presentationLoadingCount}>
                ${options.progress.totalRows > 0
                  ? `${options.progress.loadedRows} / ${options.progress.totalRows}`
                  : `${options.progress.readyShards} / ${options.progress.totalShards}`}
              </div>`
            : nothing}
        </div>
      </div>
    `;
  }

  private syncLivePresentationCurrentView() {
    if (!this.isCanvasDatabaseStorageReady()) {
      return;
    }
    const presentation = this.resolveCanvasPresentation();
    if (presentation.kind !== 'live') {
      return;
    }

    const dataSource = this.canvasDataSource.value;
    if (dataSource.viewManager.currentViewId$.value === presentation.view.id) {
      return;
    }
    dataSource.setTransientCurrentView(presentation.view.id);
  }

  private renderLivePresentation(view: ViewBasicDataType) {
    const dataSource = this.canvasDataSource.value;
    const viewManager = dataSource.viewManager;
    const supportsLiveView = LIVE_CANVAS_VIEW_MODES.has(view.mode);
    const currentViewMatches = viewManager.currentViewId$.value === view.id;
    const currentViewLogic =
      supportsLiveView && currentViewMatches
        ? this.canvasDataViewRootLogic.value.currentView$.value
        : undefined;
    const setupWizardOpen = currentViewLogic?.isSetupWizardOpen() ?? false;
    const readiness = resolveCanvasDatabasePresentationReadiness({
      liveViewLogicReady: !supportsLiveView || !!currentViewLogic,
      liveViewLogicRequired: supportsLiveView,
      presentationKind: 'live',
      storageReady: true,
    });
    if (readiness.status === 'ready' && currentViewLogic) {
      this.markCanvasDatabaseLoadOnce('first-live-render', {
        viewId: view.id,
        viewMode: view.mode,
      });
    }
    if (supportsLiveView && readiness.status === 'loading') {
      return this.renderLoadingPresentation({ showViewSwitcher: false });
    }
    if (!supportsLiveView) {
      return html`
        <div
          class=${styles.presentationContainer}
          data-data-view-frame="true"
          data-testid="edgeless-database-presentation"
          data-view-mode=${view.mode}
        >
          ${this.renderCanvasPresentationHeader(view, {
            showDataViewToolbar: false,
            showViewSwitcher: true,
          })}
          <div
            data-canvas-db-zone="internal-window"
            data-data-view-frame-body="true"
            class=${classMap({
              [styles.presentationBody]: true,
              [styles.presentationBodyInteractive]:
                this.isCanvasPresentationInteractive,
            })}
            @pointerdown=${this.stopCanvasEventWhenEditing}
            @wheel=${this.stopCanvasEventWhenEditing}
          >
            ${this.renderUnsupportedCanvasView(view)}
          </div>
        </div>
      `;
    }

    return html`
      <div
        class=${styles.presentationContainer}
        data-testid="edgeless-database-presentation"
        data-view-mode=${view.mode}
      >
        <div
          ${ref(this.attachPresentationScrollHost)}
          data-canvas-db-zone="content-edit"
          data-live-view-setup-open=${setupWizardOpen ? 'true' : 'false'}
          class=${classMap({
            [styles.liveDataViewSurface]: true,
            [styles.liveDataViewSurfaceRestoring]:
              !this._presentationViewportRestored,
            'database-live-presentation-viewport': true,
            'custom-theme-override': true,
          })}
          @pointerdown=${this.stopCanvasEventWhenEditing}
          @wheel=${this.stopCanvasEventWhenEditing}
        >
          ${currentViewLogic
            ? html` ${this.canvasDataViewRootLogic.value.render()} `
            : nothing}
        </div>
      </div>
    `;
  }

  private renderCanvasPresentationHeader(
    view?: ViewBasicDataType,
    options: {
      showDataViewToolbar?: boolean;
      showFullscreenButton?: boolean;
      showViewSwitcher?: boolean;
    } = {}
  ) {
    const rawTitle = this.isCanvasDatabaseStorageReady()
      ? this.canvasTitleValue
      : '';
    const titleText = rawTitle || 'Untitled';
    const showFullscreenButton = options.showFullscreenButton ?? true;
    const showDataViewToolbar = options.showDataViewToolbar ?? false;
    const showViewSwitcher = options.showViewSwitcher ?? true;
    const titleRow = this.renderCanvasEditableTitle(rawTitle, titleText);

    return html`
      <div
        class=${styles.presentationHeader}
        data-canvas-db-zone="select-move"
        data-data-view-frame-top="true"
        @pointerdown=${this.rememberCanvasHeaderPointerDown}
        @click=${this.fitCanvasDatabaseFromHeaderClick}
      >
        ${showDataViewToolbar
          ? this.renderCanvasDataViewToolbarFromRoot({
              summaryActive: false,
              titleRow,
              trailingActions: html`
                ${this.renderDatabaseOps()}
                ${showFullscreenButton
                  ? this.renderFullscreenButton()
                  : nothing}
              `,
            })
          : html`<div class=${styles.presentationHeaderTitleRow}>
              ${titleRow}
              <div
                class=${styles.summaryHeaderActions}
                data-canvas-db-zone="toolbar-action"
              >
                ${showViewSwitcher
                  ? this.renderCanvasViewSwitcher(view?.id)
                  : nothing}
                ${showFullscreenButton
                  ? this.renderFullscreenButton()
                  : nothing}
              </div>
            </div>`}
      </div>
    `;
  }

  private renderCanvasViewSwitcher(activeViewId?: string) {
    const views = this.canvasPresentationViews;
    if (!views.length) {
      return nothing;
    }
    const activeView =
      views.find(view => view.id === activeViewId) ?? views[0] ?? undefined;
    if (!activeView) {
      return nothing;
    }
    const activeLabel = activeView.name || normalizeTypeLabel(activeView.mode);
    const activeIcon = this.canvasDataSource.value.viewMetaGetById(
      activeView.id
    )?.renderer.icon;

    return html`
      <div class=${styles.presentationViewSwitcher}>
        <button
          class=${styles.presentationViewSelect}
          data-canvas-db-zone="toolbar-action"
          title=${`${activeLabel} (${activeView.mode})`}
          @click=${(event: MouseEvent) =>
            this.openCanvasViewSwitcher(event, activeView.id)}
        >
          ${activeIcon
            ? html`<uni-lit
                class=${styles.presentationViewSelectIcon}
                .uni=${activeIcon}
              ></uni-lit>`
            : nothing}
          <span>${activeLabel}</span>
          ${ArrowDownSmallIcon()}
        </button>
      </div>
    `;
  }

  private openCanvasViewSwitcher(event: MouseEvent, activeViewId: string) {
    event.preventDefault();
    event.stopPropagation();
    const views = this.canvasPresentationViews;
    popFilterableSimpleMenu(
      popupTargetFromElement(event.currentTarget as HTMLElement),
      [
        menu.group({
          items: views.map(view => {
            const label = view.name || normalizeTypeLabel(view.mode);
            const icon = this.canvasDataSource.value.viewMetaGetById(view.id)
              ?.renderer.icon;
            const viewStillExists = () =>
              this.canvasPresentationViews.some(candidate => {
                return candidate.id === view.id;
              });
            return menu.action({
              name: label,
              prefix: icon ? html`<uni-lit .uni=${icon}></uni-lit>` : undefined,
              isSelected: view.id === activeViewId,
              hide: () => !viewStillExists(),
              select: () => {
                if (!viewStillExists()) {
                  return;
                }
                this.setLiveCanvasPresentation(view.id);
              },
            });
          }),
        }),
      ]
    );
  }

  private renderUnsupportedCanvasView(view: ViewBasicDataType) {
    return html`
      <div class=${styles.presentationInvalid}>
        <div class=${styles.presentationInvalidTitle}>
          ${normalizeTypeLabel(view.mode)} canvas view is unsupported
        </div>
        <div class=${styles.presentationInvalidText}>
          Table, Kanban, Gantt, and Calendar render live on canvas.
        </div>
      </div>
    `;
  }

  private readonly switchToSummaryPresentation = (event?: MouseEvent) => {
    event?.stopPropagation();
    this.clearPendingPresentationViewport();
    this.resetHeightCap();
    this.setCanvasUiPresentation(createCanvasSummaryPresentationState());
  };

  private setLiveCanvasPresentation(viewId: string) {
    this.clearPendingPresentationViewport();
    this.resetHeightCap();
    this.setCanvasUiPresentation(createCanvasLivePresentationState(viewId));
  }

  private setCanvasUiPresentation(state: CanvasDatabasePresentationState) {
    this.markCanvasPresentationKey(this.getCanvasPresentationState());
    this.markCanvasPresentationKey(state);
    this.canvasLivePresentationState$.value = state;
    if (state.mode === 'live') {
      this.canvasDataSource.value.setTransientCurrentView(state.viewId);
    } else {
      this.canvasDataSource.value.setTransientCurrentView(undefined);
    }
    updateCanvasPresentationState(this.canvasPresentationModel, state);
    this.publishCanvasUiAwareness(state);
    this.syncCanvasDatabaseBoundWithActivePresentationHeight();
    this.requestUpdate();
    this._queueCanvasHeightUpdate?.();
    this.queuePresentationScrollTargetSync();
  }

  private get titleInputSize() {
    const length = this._titleDraft.length || 'Untitled'.length;
    return Math.min(Math.max(length, 1), 80);
  }

  private applyCanvasPresentationStateFromStorage() {
    const state = this.getCanvasPresentationStateFromStorage(
      this.canvasDataSource.value
    );
    const presentationChanged = this.markCanvasPresentationKey(state);
    this.canvasLivePresentationState$.value = state;
    if (state.mode === 'live') {
      this.canvasDataSource.value.setTransientCurrentView(state.viewId);
    } else {
      this.canvasDataSource.value.setTransientCurrentView(undefined);
    }
    return presentationChanged;
  }

  private readonly attachPresentationScrollHost = (
    element?: Element | null
  ) => {
    const next = element instanceof HTMLElement ? element : null;
    if (next === this._presentationScrollHost) {
      return;
    }
    this.cleanupPresentationScrollHostObservers();
    this.cleanupPresentationScrollTargets();
    this._presentationScrollHost = next;
    if (next) {
      this.startPresentationViewportRestore();
      this.setupPresentationScrollHostObservers(next);
    } else {
      this.finishPresentationViewportRestore();
    }
    this.queuePresentationScrollTargetSync();
  };

  private readonly activateCanvasSelectionFromPresentation = (
    event: PointerEvent
  ) => {
    if (this.isCanvasToolCapturingPresentation) {
      return;
    }
    if (
      !shouldActivateCanvasDatabaseSelectionFromPointer({
        button: event.button,
        editing: this._editing,
        hasRangeModifier: event.shiftKey || event.ctrlKey || event.metaKey,
        isLocked: this.model.isLocked(),
        isPrimary: event.isPrimary,
        selected: this._selected,
      })
    ) {
      return;
    }

    this.setCanvasDatabaseGfxSelection();
  };

  private setCanvasDatabaseGfxSelection() {
    this.gfx.selection.set({
      elements: [this.model.id],
      editing: false,
    });
  }

  private clearPendingPresentationViewport() {
    if (this._presentationScrollTimer) {
      clearTimeout(this._presentationScrollTimer);
      this._presentationScrollTimer = null;
    }
    this._pendingPresentationViewId = null;
    this._lastPresentationScrollTarget = PRESENTATION_ROOT_SCROLL_TARGET;
    this._pendingPresentationViewportTargets = {};
    this._lastLocalPresentationViewport = null;
    this.clearSuppressedPresentationScrollTargets();
  }

  private clearPresentationViewportRestoreTimers() {
    if (this._presentationViewportRestoreReadyTimer) {
      clearTimeout(this._presentationViewportRestoreReadyTimer);
      this._presentationViewportRestoreReadyTimer = null;
    }
    if (this._presentationViewportRestoreMaxTimer) {
      clearTimeout(this._presentationViewportRestoreMaxTimer);
      this._presentationViewportRestoreMaxTimer = null;
    }
  }

  private finishPresentationViewportRestore() {
    this.clearPresentationViewportRestoreTimers();
    if (!this._presentationViewportRestored) {
      this._presentationViewportRestored = true;
    }
  }

  private schedulePresentationViewportRestoreReady() {
    if (this._presentationViewportRestored) {
      return;
    }
    if (this._presentationViewportRestoreReadyTimer) {
      clearTimeout(this._presentationViewportRestoreReadyTimer);
    }
    this._presentationViewportRestoreReadyTimer = setTimeout(() => {
      this.finishPresentationViewportRestore();
    }, 140);
  }

  private startPresentationViewportRestore() {
    const state = this.getCanvasPresentationState();
    if (state.mode !== 'live' || !state.viewport) {
      this.finishPresentationViewportRestore();
      return;
    }
    this.clearPresentationViewportRestoreTimers();
    this._presentationViewportRestored = false;
    this._presentationViewportRestoreMaxTimer = setTimeout(() => {
      this.finishPresentationViewportRestore();
    }, 2000);
  }

  private readonly onPresentationScrollTarget = (
    targetId: string,
    target: HTMLElement,
    position?: CanvasDatabasePresentationViewportTarget
  ) => {
    if (this._isApplyingPresentationViewport) {
      return;
    }
    if (this.consumeSuppressedPresentationScrollTarget(targetId)) {
      return;
    }
    const state = this.getCanvasPresentationState();
    if (state.mode !== 'live' || !state.viewId) {
      return;
    }
    this._pendingPresentationViewId = state.viewId;
    this._lastPresentationScrollTarget = targetId;
    this._pendingPresentationViewportTargets = {
      ...this._pendingPresentationViewportTargets,
      [targetId]: {
        ...position,
        scrollLeft: position?.scrollLeft ?? target.scrollLeft,
        scrollTop: position?.scrollTop ?? target.scrollTop,
      },
    };
    if (this._presentationScrollTimer) {
      clearTimeout(this._presentationScrollTimer);
    }
    this._presentationScrollTimer = setTimeout(() => {
      this._presentationScrollTimer = null;
      const latestState = this.getCanvasPresentationState();
      if (
        latestState.mode !== 'live' ||
        !latestState.viewId ||
        latestState.viewId !== this._pendingPresentationViewId
      ) {
        this._pendingPresentationViewportTargets = {};
        this._pendingPresentationViewId = null;
        return;
      }
      const pendingTargets = this._pendingPresentationViewportTargets;
      this._pendingPresentationViewportTargets = {};
      this._pendingPresentationViewId = null;
      const targets = {
        ...(latestState.viewport?.targets ?? {}),
        ...pendingTargets,
      };
      const activeTarget =
        targets[this._lastPresentationScrollTarget] ??
        pendingTargets[Object.keys(pendingTargets)[0] ?? ''];
      if (!activeTarget) {
        return;
      }
      const nextViewport = {
        ...activeTarget,
        target: this._lastPresentationScrollTarget,
        targets,
      };
      const updatedAt = Date.now();
      this._lastLocalPresentationViewport = {
        updatedAt,
        viewport: nextViewport,
      };
      this.setCanvasUiPresentation({
        ...latestState,
        viewport: quantizeCanvasViewport(nextViewport),
        updatedAt,
      });
    }, CANVAS_VIEWPORT_SYNC_DELAY);
  };

  private applyPresentationViewport(options: { syncTargets?: boolean } = {}) {
    const host = this._presentationScrollHost;
    if (!host) {
      return;
    }
    const state = this.getCanvasPresentationState();
    if (state.mode !== 'live' || !state.viewport) {
      return;
    }
    if (options.syncTargets ?? true) {
      this.syncPresentationScrollTargets();
    }
    this._isApplyingPresentationViewport = true;
    let applied = false;
    const applyTarget = (
      target: HTMLElement,
      targetId: string | undefined,
      position: CanvasDatabasePresentationViewportTarget
    ) => {
      if (this.shouldSkipLocalPresentationEcho(target, targetId, position)) {
        return;
      }
      if (
        Math.abs(target.scrollLeft - position.scrollLeft) <= 2 &&
        Math.abs(target.scrollTop - position.scrollTop) <= 2
      ) {
        return;
      }
      target.scrollLeft = position.scrollLeft;
      target.scrollTop = position.scrollTop;
      if (targetId) {
        this.suppressNextPresentationScrollTarget(targetId);
      }
      applied = true;
    };
    const applyParticipantTarget = (
      targetId: string,
      position: CanvasDatabasePresentationViewportTarget
    ) => {
      const participant = this._presentationViewportParticipants.get(targetId);
      if (!participant?.applyCanvasViewportTarget) {
        return;
      }
      if (this.shouldSkipLocalPresentationParticipantEcho(targetId, position)) {
        return;
      }
      const changed = participant.applyCanvasViewportTarget(targetId, position);
      if (changed !== false) {
        applied = true;
      }
    };

    if (state.viewport.targets) {
      Object.entries(state.viewport.targets).forEach(([targetId, position]) => {
        const target = this._presentationScrollTargets.get(targetId);
        if (target) {
          applyTarget(target, targetId, position);
          return;
        }
        applyParticipantTarget(targetId, position);
      });
    }

    if (!applied && !state.viewport.targets) {
      const target =
        this._presentationScrollTargets.get(
          state.viewport.target ?? PRESENTATION_ROOT_SCROLL_TARGET
        ) ?? host;
      applyTarget(target, state.viewport.target, state.viewport);
    }

    if (applied) {
      requestAnimationFrame(() => {
        this._isApplyingPresentationViewport = false;
        this.schedulePresentationViewportRestoreReady();
      });
    } else {
      this._isApplyingPresentationViewport = false;
      this.schedulePresentationViewportRestoreReady();
    }
  }

  private applyObservedPresentationViewport() {
    if (!this.isCanvasDatabasePresentationWorkActive()) {
      return;
    }
    if (
      !shouldApplyObservedCanvasViewport(
        this._presentationScrollTimer !== null ||
          this._pendingPresentationViewId !== null
      )
    ) {
      return;
    }
    this.applyPresentationViewport({ syncTargets: false });
  }

  private syncPresentationScrollTargets() {
    if (!this.isCanvasDatabasePresentationWorkActive()) {
      return;
    }
    measureGfxPerformance(
      this.std,
      'database-viewport-sync',
      () => {
        const host = this._presentationScrollHost;
        this.cleanupPresentationScrollTargets();
        if (!host) {
          return;
        }
        const targets = this.collectPresentationScrollTargets(host);
        targets.forEach(([targetId, target]) => {
          this._presentationScrollTargets.set(targetId, target);
          const onScroll = () =>
            this.onPresentationScrollTarget(targetId, target);
          target.addEventListener('scroll', onScroll, { passive: true });
          this._presentationScrollListeners.set(target, () => {
            target.removeEventListener('scroll', onScroll);
          });
        });
        this.collectPresentationViewportParticipants(host).forEach(
          participant => {
            const participantTargets =
              participant.getCanvasViewportTargets?.() ?? {};
            Object.keys(participantTargets).forEach(targetId => {
              this._presentationViewportParticipants.set(targetId, participant);
            });
            const onViewportTargetScroll = (event: Event) => {
              const { position, targetId } =
                (event as CanvasViewportTargetEvent).detail ?? {};
              if (!targetId || !position) {
                return;
              }
              event.stopPropagation();
              this.onPresentationScrollTarget(targetId, participant, position);
            };
            participant.addEventListener(
              'canvas-viewport-target-scroll',
              onViewportTargetScroll
            );
            this._presentationScrollListeners.set(participant, () => {
              participant.removeEventListener(
                'canvas-viewport-target-scroll',
                onViewportTargetScroll
              );
            });
          }
        );
      },
      () =>
        this._presentationScrollTargets.size +
        this._presentationViewportParticipants.size
    );
  }

  private cleanupPresentationScrollTargets() {
    this._presentationScrollListeners.forEach(cleanup => cleanup());
    this._presentationScrollListeners.clear();
    this._presentationScrollTargets.clear();
    this._presentationViewportParticipants.clear();
    this.clearSuppressedPresentationScrollTargets();
  }

  private clearSuppressedPresentationScrollTargets() {
    this._suppressedPresentationScrollTargetTimers.forEach(timer =>
      clearTimeout(timer)
    );
    this._suppressedPresentationScrollTargetTimers.clear();
    this._suppressedPresentationScrollTargets.clear();
  }

  private consumeSuppressedPresentationScrollTarget(targetId: string): boolean {
    if (!this._suppressedPresentationScrollTargets.delete(targetId)) {
      return false;
    }
    const timer = this._suppressedPresentationScrollTargetTimers.get(targetId);
    if (timer) {
      clearTimeout(timer);
      this._suppressedPresentationScrollTargetTimers.delete(targetId);
    }
    return true;
  }

  private suppressNextPresentationScrollTarget(targetId: string) {
    this._suppressedPresentationScrollTargets.add(targetId);
    const previous =
      this._suppressedPresentationScrollTargetTimers.get(targetId);
    if (previous) {
      clearTimeout(previous);
    }
    this._suppressedPresentationScrollTargetTimers.set(
      targetId,
      setTimeout(() => {
        this._suppressedPresentationScrollTargets.delete(targetId);
        this._suppressedPresentationScrollTargetTimers.delete(targetId);
      }, 250)
    );
  }

  private cleanupPresentationScrollHostObservers() {
    if (this._presentationTargetSyncFrame !== null) {
      cancelAnimationFrame(this._presentationTargetSyncFrame);
      this._presentationTargetSyncFrame = null;
    }
    this._presentationInteractionCleanup?.();
    this._presentationInteractionCleanup = null;
    this._presentationParticipantReadyCleanup?.();
    this._presentationParticipantReadyCleanup = null;
    this._presentationTargetResizeObserver?.disconnect();
    this._presentationTargetResizeObserver = null;
  }

  private setupPresentationScrollHostObservers(host: HTMLElement) {
    const onParticipantReady = () => {
      this.queuePresentationScrollTargetSync();
    };
    host.addEventListener(
      'pointerdown',
      this.activateCanvasSelectionFromPresentation,
      {
        capture: true,
      }
    );
    this._presentationInteractionCleanup = () => {
      host.removeEventListener(
        'pointerdown',
        this.activateCanvasSelectionFromPresentation,
        {
          capture: true,
        }
      );
    };
    host.addEventListener(
      CANVAS_VIEWPORT_PARTICIPANT_READY_EVENT,
      onParticipantReady
    );
    this._presentationParticipantReadyCleanup = () => {
      host.removeEventListener(
        CANVAS_VIEWPORT_PARTICIPANT_READY_EVENT,
        onParticipantReady
      );
    };
    this._presentationTargetResizeObserver = new ResizeObserver(() => {
      if (!this.isCanvasDatabasePresentationWorkActive()) {
        return;
      }
      this.queuePresentationScrollTargetSync();
    });
    this._presentationTargetResizeObserver.observe(host);
  }

  private queuePresentationScrollTargetSync() {
    if (!this.isCanvasDatabasePresentationWorkActive()) {
      return;
    }
    if (
      !this._presentationViewportRestored &&
      this._presentationViewportRestoreReadyTimer
    ) {
      clearTimeout(this._presentationViewportRestoreReadyTimer);
      this._presentationViewportRestoreReadyTimer = null;
    }
    if (this._presentationTargetSyncFrame !== null) {
      return;
    }
    this._presentationTargetSyncFrame = requestAnimationFrame(() => {
      this._presentationTargetSyncFrame = null;
      this.syncPresentationScrollTargets();
      this.applyObservedPresentationViewport();
    });
  }

  private collectPresentationViewportParticipants(
    host: HTMLElement
  ): CanvasViewportParticipant[] {
    return [
      ...host.querySelectorAll<CanvasViewportParticipant>(
        CANVAS_VIEWPORT_PARTICIPANT_SELECTOR
      ),
    ].filter(
      participant =>
        typeof participant.getCanvasViewportTargets === 'function' ||
        typeof participant.applyCanvasViewportTarget === 'function'
    );
  }

  private collectPresentationScrollTargets(
    host: HTMLElement
  ): Array<[string, HTMLElement]> {
    return [[PRESENTATION_ROOT_SCROLL_TARGET, host]];
  }

  private shouldSkipLocalPresentationEcho(
    target: HTMLElement,
    targetId: string | undefined,
    position: CanvasDatabasePresentationViewportTarget
  ): boolean {
    const state = this.getCanvasPresentationState();
    const local = this._lastLocalPresentationViewport;
    if (
      state.mode !== 'live' ||
      !state.updatedAt ||
      !local ||
      state.updatedAt !== local.updatedAt
    ) {
      return false;
    }
    const localPosition = targetId
      ? local.viewport.targets?.[targetId]
      : local.viewport;
    if (!localPosition) {
      return false;
    }
    const quantizedLocalPosition = targetId
      ? quantizeCanvasViewport({
          scrollLeft: 0,
          scrollTop: 0,
          targets: {
            [targetId]: localPosition,
          },
        }).targets?.[targetId]
      : quantizeCanvasViewport(local.viewport);
    if (
      !quantizedLocalPosition ||
      Math.abs(quantizedLocalPosition.scrollLeft - position.scrollLeft) > 2 ||
      Math.abs(quantizedLocalPosition.scrollTop - position.scrollTop) > 2
    ) {
      return false;
    }
    return (
      Math.abs(target.scrollLeft - localPosition.scrollLeft) <= 2 &&
      Math.abs(target.scrollTop - localPosition.scrollTop) <= 2
    );
  }

  private shouldSkipLocalPresentationParticipantEcho(
    targetId: string,
    position: CanvasDatabasePresentationViewportTarget
  ): boolean {
    const state = this.getCanvasPresentationState();
    const local = this._lastLocalPresentationViewport;
    if (
      state.mode !== 'live' ||
      !state.updatedAt ||
      !local ||
      state.updatedAt !== local.updatedAt
    ) {
      return false;
    }
    const localPosition = local.viewport.targets?.[targetId];
    const participant = this._presentationViewportParticipants.get(targetId);
    const currentPosition = participant?.getCanvasViewportTargets?.()[targetId];
    if (!localPosition || !currentPosition) {
      return false;
    }
    const quantizedLocalPosition = quantizeCanvasViewport({
      scrollLeft: 0,
      scrollTop: 0,
      targets: {
        [targetId]: localPosition,
      },
    }).targets?.[targetId];
    if (
      !quantizedLocalPosition ||
      Math.abs(quantizedLocalPosition.scrollLeft - position.scrollLeft) > 2 ||
      Math.abs(quantizedLocalPosition.scrollTop - position.scrollTop) > 2
    ) {
      return false;
    }
    return (
      Math.abs(currentPosition.scrollLeft - localPosition.scrollLeft) <= 2 &&
      Math.abs(currentPosition.scrollTop - localPosition.scrollTop) <= 2
    );
  }

  private readonly stopCanvasEventWhenEditing = (event: Event) => {
    if (event instanceof WheelEvent && (event.ctrlKey || event.metaKey)) {
      return;
    }
    if (this.isCanvasPresentationInteractive) {
      event.stopPropagation();
    }
  };

  private readonly stopCanvasWheelInInternalWindow = (event: WheelEvent) => {
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    event.stopPropagation();
  };

  private readonly onTitleClick = (event: MouseEvent) => {
    if (this.canvasDataSource.value.readonly$.value) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const state = this.getCanvasDatabaseRuntimeState();
    if (!state || state.model.isLocked()) {
      return;
    }
    state.gfx.selection.set({
      elements: [state.model.id],
      editing: true,
    });
    this._isEditingTitle = true;
    this._titleDraft = this.canvasTitleValue;
    this.requestDataViewFrameUpdate();
    const titleRendererUpdate =
      this.canvasDataViewRootLogic.value.dataViewRenderer?.updateComplete ??
      this.updateComplete;
    titleRendererUpdate
      .then(() => {
        requestAnimationFrame(() => {
          if (!this._isEditingTitle) {
            return;
          }
          const input = this.renderRoot.querySelector(
            `.${styles.summaryTitleInput}`
          ) as HTMLInputElement | null;
          input?.focus();
          input?.select();
        });
      })
      .catch(console.error);
  };

  private readonly stopTitleEditorPointer = (event: PointerEvent) => {
    event.stopPropagation();
  };

  private readonly onTitleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this._titleDraft = target.value;
  };

  private readonly onTitleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitTitle();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this._isEditingTitle = false;
      this._titleDraft = this.canvasTitleValue;
      this.requestDataViewFrameUpdate();
      return;
    }
    // Stop propagation to prevent edgeless keyboard manager from
    // deleting the block while editing the title
    event.stopPropagation();
  };

  private readonly onTitleBlur = () => {
    this.commitTitle();
  };

  private commitTitle() {
    if (!this._isEditingTitle) {
      return;
    }
    this._isEditingTitle = false;
    this.requestDataViewFrameUpdate();
    const storageModel = this.canvasDataSource.value.storageModel;
    const titleText = storageModel.props.title;
    if (!titleText.yText.doc) {
      // Block was deleted — Y.Text detached, nothing to commit
      return;
    }
    const nextTitle = this._titleDraft.trim();
    storageModel.store.captureSync();
    storageModel.store.transact(() => {
      titleText.replace(0, titleText.yText.length, nextTitle);
    });
  }

  // --- Drag & Drop / Paste handlers ---

  private _setDropHighlight(active: boolean) {
    const container = this.renderRoot.querySelector(
      '[data-testid="edgeless-database-container"]'
    );
    if (!container) return;
    if (active) {
      container.setAttribute('data-drop-target', 'true');
    } else {
      container.removeAttribute('data-drop-target');
    }
  }

  /**
   * Registers this database block as a drop target in the existing DnD system
   * (std.dnd / @atlaskit/pragmatic-drag-and-drop). This hooks into the same
   * system used by the 6-dot drag handle widget.
   */
  private _registerAsDropTarget() {
    this._disposables.add(
      this.std.dnd.dropTarget({
        element: this,
        getIsSticky: () => false,
        canDrop: ({ source }) => {
          if (isFileIndexDatabase(this.model)) return false;
          const payload = source.data as DragBlockPayload;
          const entity = payload.bsEntity;
          if (entity?.type !== 'blocks' || !entity.snapshot) return false;
          return entity.snapshot.content.some(isImportableBlock);
        },
        onDragEnter: () => this._setDropHighlight(true),
        onDragLeave: () => this._setDropHighlight(false),
        onDrop: ({ source }) => {
          this._setDropHighlight(false);
          try {
            this._handleBlockDrop(source.data as DragBlockPayload);
          } catch (error) {
            this._handleImportFilesError(error);
          }
        },
      })
    );
  }

  /**
   * Handles a drag handle drop: extracts blobs from the snapshot,
   * creates database rows, and deletes the source elements from canvas.
   */
  private _handleBlockDrop(payload: DragBlockPayload) {
    if (isFileIndexDatabase(this.model)) return;
    const entity = payload.bsEntity;
    if (!entity?.snapshot) return;

    const store = this.std.store;
    const sameDoc = payload.from?.docId === store.id;

    const blobs: AttachmentBlobInfo[] = [];
    for (const block of entity.snapshot.content) {
      const blob = extractBlobFromSnapshot(block);
      if (blob) {
        blobs.push(blob);
      }
    }
    if (blobs.length === 0) return;
    assertCanAppendAttachmentRows(
      this.model,
      blobs.length,
      'Canvas block drop'
    );
    for (const blob of blobs) {
      addRowWithAttachment(store, this.model, blob);
    }

    // Delete source elements from canvas (same-doc only)
    if (sameDoc && entity.modelIds) {
      for (const modelId of entity.modelIds) {
        const blockEntry = store.getBlock(modelId);
        if (blockEntry) {
          store.deleteBlock(blockEntry.model);
        }
      }
    }

    this.updateSummaryCounts();
  }

  /**
   * Tries to import blocks from BlockSuite's internal clipboard format.
   * Handles copied image/attachment blocks that use the `blocksuite/surface`
   * MIME type (embedded in text/html via data-blocksuite-snapshot).
   *
   * @returns true if blocks were imported, false otherwise
   */
  private _importFromBlockSuiteClipboard(data: DataTransfer): boolean {
    try {
      const json = this.std.clipboard.readFromClipboard(data);
      const surfaceDataJson = json['blocksuite/surface'];
      if (!surfaceDataJson) return false;

      const { snapshot } = JSON.parse(surfaceDataJson) as {
        snapshot?: Record<string, unknown>[];
      };
      if (!Array.isArray(snapshot)) return false;

      const blobs: AttachmentBlobInfo[] = [];
      for (const block of snapshot) {
        const blob = extractBlobFromSnapshot(block as BlockSnapshot);
        if (blob) {
          blobs.push(blob);
        }
      }
      if (blobs.length === 0) return false;
      if (offerBlobReferencesToSelectedAttachmentCell(blobs)) {
        return true;
      }
      assertCanAppendAttachmentRows(
        this.model,
        blobs.length,
        'BlockSuite clipboard import'
      );
      for (const blob of blobs) {
        addRowWithAttachment(this.std.store, this.model, blob);
      }

      this.updateSummaryCounts();
      return true;
    } catch (error) {
      if (error instanceof RangeError) {
        throw error;
      }
      return false;
    }
  }

  private _notifyImportError(error: unknown): void {
    const message =
      error instanceof Error
        ? error.message
        : 'Cannot import clipboard content into database';
    const notification = this.std.getOptional(NotificationProvider);
    if (notification) {
      notification.toast(message);
    } else {
      toast(this.host, message);
    }
  }

  private _handleImportFilesError(error: unknown): void {
    if (error instanceof RangeError) {
      this._notifyImportError(error);
      return;
    }
    console.error(error);
  }

  private _isDatabaseSelected(): boolean {
    return this.gfx.selection.has(this.model.id);
  }

  private getSelectedAttachmentCellContainer(): HTMLElement | null {
    const selection = this.canvasViewSelection$.value;
    if (
      !selection ||
      selection.type !== 'table' ||
      selection.selectionType !== 'area'
    ) {
      return null;
    }

    const view = this.canvasDataSource.value.viewManager.viewGet(
      selection.viewId
    );
    if (!(view instanceof TableSingleView)) {
      return null;
    }

    const columnId = view.propertyIds$.value[selection.focus.columnIndex];
    if (!columnId) {
      return null;
    }

    const column = view.propertyGetOrCreate(columnId);
    if (column.type$.value !== 'attachment') {
      return null;
    }

    const rows = selection.groupKey
      ? view.groupTrait.groupDataMap$.value?.[selection.groupKey]?.rows
      : view.getBoundedRowsWindow().rows;
    const rowId = rows?.[selection.focus.rowIndex]?.rowId;
    if (!rowId) {
      return null;
    }

    const rowSelector = CSS.escape(rowId);
    const columnSelector = CSS.escape(columnId);
    return this.querySelector<HTMLElement>(
      `dv-table-view-cell-container[data-row-id="${rowSelector}"][data-column-id="${columnSelector}"], ` +
        `affine-database-virtual-cell-container[data-row-id="${rowSelector}"][data-column-id="${columnSelector}"]`
    );
  }

  private pasteFilesIntoSelectedAttachmentCell(files: File[]): boolean {
    const cell = this.getSelectedAttachmentCellContainer();
    if (!cell) {
      return false;
    }

    cell.dispatchEvent(
      new CustomEvent('database-paste-files', {
        detail: { files },
        bubbles: false,
      })
    );
    return true;
  }

  private async _importFiles(files: File[]): Promise<void> {
    assertCanAppendAttachmentRows(
      this.model,
      files.length,
      'Canvas file import'
    );
    const store = this.std.store;
    for (const file of files) {
      const blob = await uploadFileAsBlob(this.std, file);
      addRowWithAttachment(store, this.model, blob);
    }
    this.updateSummaryCounts();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_EDGELESS_DATABASE]: EdgelessDatabaseBlockComponent;
  }
}

// Interaction Extension for Database on Canvas
export const DatabaseBlockInteraction =
  GfxViewInteractionExtension<EdgelessDatabaseBlockComponent>(
    DatabaseBlockSchema.model.flavour,
    {
      selectionBorderRadius: 12,
      resizeConstraint: {
        allowedHandlers: ['left', 'right'],
        minWidth: 280,
        minHeight: MIN_CANVAS_DATABASE_HEIGHT,
        maxHeight: MAX_CANVAS_DATABASE_HEIGHT,
      },
      handleRotate: () => {
        return {
          beforeRotate(context) {
            context.set({
              rotatable: false,
            });
          },
        };
      },
      handleSelection: ({ gfx, view, model }) => {
        return {
          onSelect(context) {
            const { event, selected, multiSelect } = context;
            const { editing } = gfx.selection;
            const alreadySelected = gfx.selection.has(model.id);
            const isAction = event.composedPath().some(target => {
              if (!(target instanceof Element)) return false;
              const zone = target.getAttribute('data-canvas-db-zone');
              return zone === 'toolbar-action' || zone === 'fullscreen-action';
            });

            if (
              !multiSelect &&
              selected &&
              shouldKeepCanvasDatabaseActionSelectionNonEditing({
                button: event.button,
                hasRangeModifier:
                  event.shiftKey || event.ctrlKey || event.metaKey,
                isAction,
                isLocked: model.isLocked(),
                isPrimary: event.isPrimary,
              })
            ) {
              gfx.selection.set({
                elements: [model.id],
                editing: false,
              });
              return true;
            }

            if (!multiSelect && selected && (alreadySelected || editing)) {
              if (model.isLocked()) return;

              if (alreadySelected && editing) {
                return;
              }

              gfx.selection.set({
                elements: [model.id],
                editing: true,
              });

              // CRITICAL: Wait for render to complete before enabling interactions
              view.updateComplete
                .then(() => {
                  if (!view.isConnected) {
                    return;
                  }
                  // Database is ready for cell interactions
                })
                .catch(console.error);
            } else {
              context.default(context);
            }

            return;
          },
        };
      },
    }
  );
